import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ApplicationRef, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { Observable, Subject, firstValueFrom, of } from 'rxjs';
import { catchError, map, skip, take, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { PatientResponse, PatientSummary } from '../models/patient.model';
import { PatientsService } from '../services/patients.service';
import {
    MedicalAnalysis,
    PatientInfo,
    PauseRecordingDraftRequest,
    PausedCheckpointStatus,
    RecordingService,
    TranscriptionResponse
} from '../services/recording.service';
import {
    TranscriptionsService,
    TranscriptionDetailResponse,
    isCheckpointFinalRecordingKey,
    parseCheckpointFinalRecordingUuid
} from '../transcriptions/transcriptions.service';
import {
    RecordingCheckpointState,
    clearLocalStorageForPausedTranscriptionDraft,
    clearRecordingCheckpoint,
    clearRecordingDraftForId,
    createRecordingUuid,
    mergePauseVisitIntoManifest,
    readRecordingCheckpoint,
    readRecordingCheckpointById,
    releaseActiveCheckpointForNewVisit,
    restoreRecordingCheckpointManifest,
    removeChunkKeysFromIndex,
    removeChunkKeysRange,
    removeChunkKeysRangeAsync,
    loadRecordingChunkLocal,
    saveRecordingChunkLocal,
    writeCheckpointUploadProgress,
    writeRecordingDraftStarted,
    warmUpRecordingChunkStorage
} from './recording-checkpoint.storage';
import {
  browserCanContinueCheckpointContainer,
  checkpointTimesliceMs,
  chunksPerCheckpointUpload,
  fileExtensionForMimeType,
  firstChunkIndexPendingUpload,
  mimeTypeForCheckpointExtension,
  pickRecorderFormatForPlatform,
  recorderFormatForMimeType,
  shouldUseSafariMp4Recording,
  usesFragmentedMp4Checkpoints
} from './recording-codec';
import { RecordingSessionService } from './recording-session.service';
import { prefersCompactShell } from '../utils/layout.util';

/** Below this many seconds (visit timer), offer resume / start over instead of uploading for transcription. */
const MIN_RECORDING_SECONDS_FOR_UPLOAD = 30;

interface AudioChunk {
  data: Blob;
  timestamp: number;
}

interface MicrophoneChoice {
  deviceId: string;
  label: string;
}

/** Last input the user picked on this browser. Absent means the system default. */
const AUDIO_INPUT_PREF_KEY = 'scryber.audioInputId';

interface AutocompletePatientSummary extends PatientSummary {
  nameOnly: string;
}

@Component({
  selector: 'app-recording',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    AutoCompleteModule,
    ToastModule,
    DialogModule
  ],
  templateUrl: './recording.component.html',
  styleUrls: ['./recording.component.scss'],
  providers: [MessageService, RecordingService],
  changeDetection: ChangeDetectionStrategy.Default
})
export class RecordingComponent implements OnInit, OnDestroy {
  /** Exposed for the short-recording dialog copy (seconds). */
  readonly minRecordingSecondsForUpload = MIN_RECORDING_SECONDS_FOR_UPLOAD;
  private readonly defaultPatientName = 'Unknown';
  private readonly defaultAppointmentType = 'Initial Consultation';
  private readonly defaultNeurologicalSpecialty = 'General Neurology';
  
  patientInfo: PatientInfo = {
    patientName: '',
    mrn: '',
    appointmentType: this.defaultAppointmentType,
    neurologicalSpecialty: this.defaultNeurologicalSpecialty
  };

  // Patient autocomplete properties
  allPatients: AutocompletePatientSummary[] = [];
  filteredPatients: AutocompletePatientSummary[] = [];
  selectedPatient: PatientSummary | null = null;
  selectedPatientDisplay: string = '';
  isMrnReadonly = false;
  showPatientDropdown = false;
  /** True while Create New Patient is creating the row before auto-start. */
  creatingPatientFromName = false;
  private destroy$ = new Subject<void>();
  
  // Recording states
  isRecording = false;
  isPaused = false;
  hasAttemptedRecording = false;
  recordingTime = '00:00';
  recordingInterval: any = null;
  
  // Transcription
  transcriptionText = '';
  isTranscribing = false;
  analysisResult: MedicalAnalysis | null = null;
  
  // Audio recording
  mediaRecorder: MediaRecorder | null = null;
  audioChunks: AudioChunk[] = [];
  audioStream: MediaStream | null = null;
  /** Shown once the browser can list audio inputs (after permission, labels are filled in). */
  readonly microphonePickerAvailable =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.enumerateDevices;
  microphones: MicrophoneChoice[] = [];
  microphoneMenuOpen = false;
  microphoneSwitching = false;
  /** User-chosen device id. Null follows the system default. */
  private preferredMicrophoneId: string | null = null;
  private microphoneLabelsReady = false;
  /** Live capture could not retarget yet (for example the visit was paused mid-switch). */
  private microphoneRetargetPending = false;
  recordingStartTime: number = 0;
  totalRecordingTime: number = 0;
  pausedTime: number = 0;
  private hasSavedCurrentRecording = false;
  private isStoppingRecording = false;
  /** One UUID per recording start — draft key for checkpoints and future resume-after-navigate. */
  private recordingId: string | null = null;
  /** When false, do not schedule periodic server uploads (still persist each slice to localStorage). */
  private periodicFlushActive = false;
  /** Safari fMP4: rotate {@link MediaRecorder} every 2 minutes (timeslice and {@code requestData} are unsupported). */
  private safariSegmentTimer: ReturnType<typeof setTimeout> | null = null;
  private safariSegmentStopPending = false;
  /** This recorder is being replaced after a foreground return. Its {@code onstop} must not finalize the visit. */
  private captureRestartRecorder: MediaRecorder | null = null;
  private resolveCaptureRestart: (() => void) | null = null;
  private foregroundRestoreGeneration = 0;
  /** Next logical chunk index for `${recordingId}-${chunkIndex}` (monotonic; may exceed `audioChunks.length` after resume). */
  private nextChunkIndex = 0;
  /** First chunk index not yet successfully uploaded to the server. */
  private firstPendingUploadChunkIndex = 0;
  /**
   * Logical index of {@link #audioChunks}[0]. After pause/resume, new slices are pushed densely from 0
   * while {@link #nextChunkIndex} continues from the manifest — uploads must slice using this offset.
   */
  private audioChunksLogicalBase = 0;
  /** Next 1-based S3 checkpoint filename index; persisted in manifest (see {@link writeCheckpointNextBatchNumber}). */
  private nextCheckpointBatchNumber = 1;
  private persistTail: Promise<void> = Promise.resolve();
  /** Per-chunk IndexedDB persist promises — uploads must await these so deletes are not re-written by late puts. */
  private chunkPersistByIndex = new Map<number, Promise<void>>();
  /** Chains {@code MediaRecorder.stop()} → {@code ondataavailable} → persist/upload so pause-for-later does not flush too early. */
  private recorderStopIdle: Promise<void> = Promise.resolve();
  private resolveActiveRecorderStop: (() => void) | null = null;
  /**
   * Safari segmented mode calls {@code start()} with no timeslice, so {@code ondataavailable} fires exactly once per
   * {@code stop()} — and WebKit delivers it after {@code onstop}. This deferred lets the stop barrier wait for that
   * specific data event deterministically instead of polling on a timeout.
   */
  private awaitingSegmentData = false;
  private segmentDataPromise: Promise<void> = Promise.resolve();
  private resolveSegmentData: (() => void) | null = null;
  private checkpointBatchUploadInFlight = false;
  /** Ensures checkpoint multipart POSTs never overlap (periodic + stop flush share the same HttpClient pipeline). */
  private checkpointUploadGate: Promise<void> = Promise.resolve();
  /**
   * Uploads every slice still only in the browser, including a partial batch shorter than 2 minutes.
   * Runs after navigation so Pause does not wait on the network.
   */
  private pauseTailUpload: Promise<boolean> = Promise.resolve(true);
  /** Resolves when the pause tail is in local storage. The server upload continues after navigation. */
  private pauseTailPersisted: Promise<void> = Promise.resolve();
  /**
   * Server chunks were restored, but this browser records a different container. Resume would append a format
   * the stitcher cannot mix in. End still finalizes the audio already on the server.
   */
  private serverCheckpointContainerMismatch = false;
  /** True after at least one batched POST to `/api/upload/recordings/{recordingId}` succeeded (S3 has something to finalize). */
  private anyCheckpointUploadedToServer = false;
  /** When saving a paused visit for later, skip {@link #stopRecording} finalization in {@link #ngOnDestroy}. */
  private silentRecorderTeardownDone = false;
  /**
   * Stop already opened the in-progress visit and is still flushing the last recorder slice.
   * Destroy must not stop the mic tracks until that slice has been delivered.
   */
  private processingHandoff = false;
  /** Finalize checkpoint against an existing paused DB row (same browser draft). */
  private resumeTranscriptionDbId: number | null = null;
  private pendingResumeRecordingUuid: string | null = null;
  /** Completed / needs-review row to merge new checkpoint audio into (re-transcribe + new SOAP). */
  private appendTranscriptionDbId: number | null = null;
  /** Same UUID as {@code recordings/final-{uuid}.mp3} — new checkpoints reuse this S3 prefix. */
  private appendCanonicalRecordingUuid: string | null = null;
  /** Set from transcription detail when append flow loads — mic pause / pause-for-later are not supported once SOAP exists. */
  private appendVisitHasSoap = false;
  /** When true, the next {@link MediaRecorder} {@code onstop} discards audio and skips upload. */
  private discardRecordingOnNextStop = false;
  /** Paused visit to delete when Cancel discards a recording shorter than 30 seconds. */
  private pendingFullDiscardTranscriptionId: number | null = null;
  /** If set, {@link #discardRecordingOnNextStop} navigates after cleanup; {@code null} = route change already in progress. */
  private pendingDiscardNavigate: 'home' | 'transcriptions' | null = null;
  /** Append flow: recorder stopped to abandon — wait for uploads, optionally delete S3 draft chunks, clear local draft. */
  private abandonAppendAdditionOnNextStop = false;
  private abandonAppendNavigateAfter: 'transcriptions' | 'home' | 'freshRecording' | null = null;
  /** During append-abandon stop, ignore late recorder chunks (do not persist/upload). */
  private suppressChunkPersistUntilStopHandled = false;
  /**
   * While abandoning a recorder take (append discard or resumed-paused cancel), skip {@link #saveRecordingChunkLocal}
   * for work already chained on {@link #persistTail} (callbacks may capture {@code recordingId} before storage cleanup).
   */
  private blockRecorderAbandonLocalChunkPersist = false;
  /** Paused-visit resume: manifest + chunk ceiling immediately before mic starts after list resume (for Cancel = discard new take). */
  private manifestSnapshotBeforeResumedTake: RecordingCheckpointState | null = null;
  private abandonResumedPausedTakeOnNextStop = false;
  private abandonResumedPausedNavigateAfter: 'transcriptions' | 'home' | null = null;
  /** When set, {@link #finalizeAbandonAppendAfterRecorderStop} completes a route guard waiting to navigate away. */
  private resolveAppendAbandonRouteLeave: (() => void) | null = null;
  /** Single-manifest storage: preserve non-append manifest so append start/cancel cannot orphan paused resumes. */
  private preservedManifestBeforeAppend: RecordingCheckpointState | null = null;

  /** Paused visit loaded from the list; timer restored but mic not started yet — show Resume / End, not only Record. */
  get isResumedVisitIdle(): boolean {
    return (
      !this.isRecording &&
      !this.isPaused &&
      !this.isTranscribing &&
      !this.showShortRecordingModal &&
      !this.isStoppingRecording &&
      !!this.pendingResumeRecordingUuid &&
      this.resumeTranscriptionDbId != null
    );
  }

  /** Extending a completed/processed visit (appendTranscription query) — includes while actively recording. */
  get isAppendVisitFlow(): boolean {
    return this.appendTranscriptionDbId != null && this.resumeTranscriptionDbId == null;
  }

  /** Paused visit opened via resumeTranscription (not append-to-completed). */
  get isPausedVisitResumeFlow(): boolean {
    return this.resumeTranscriptionDbId != null && this.appendTranscriptionDbId == null;
  }

  /** Add-more-to-visit loaded; timer restored, mic not started — show Resume/End until auto-start or manual start. */
  get isAppendVisitIdle(): boolean {
    return (
      !this.isRecording &&
      !this.isPaused &&
      !this.isTranscribing &&
      !this.showShortRecordingModal &&
      !this.isStoppingRecording &&
      this.isAppendVisitFlow
    );
  }

  /** Main Record (new visit) — hidden while processing, stopping, or paused/append idle flows. */
  get showNewVisitRecordButton(): boolean {
    if (this.isTranscribing || this.isStoppingRecording) {
      return false;
    }
    if (this.isRecording || this.isResumedVisitIdle || this.isAppendVisitIdle) {
      return false;
    }
    if (
      this.isAppendVisitFlow &&
      (this.isTranscribing || this.isStoppingRecording || this.showShortRecordingModal)
    ) {
      return false;
    }
    return true;
  }

  /** Append to a visit that already has SOAP: no pause-for-later and no mic pause (single continuous take until Stop). */
  get isAppendVisitSoapNoPause(): boolean {
    return this.isAppendVisitFlow && this.appendVisitHasSoap;
  }

  /** Show Pause/Resume mic control while recording (hidden for append+SOAP). */
  get showMicPauseWhileRecording(): boolean {
    return this.isRecording && !this.orphanResumeBlocksRecording && !this.isAppendVisitSoapNoPause;
  }

  /** Pause-for-later can run from Cancel — not when extending an existing visit (append-only flow). */
  get canPauseForLaterFromCancel(): boolean {
    return !this.isAppendVisitFlow;
  }
  /** True once a real name path is set for this draft (selected patient or confirmed modal); skips pause name dialog. */
  private visitPauseNameLocked = false;
  /** Unknown patient id from first Skip on this draft — reuse on later skips (no duplicate Unknown). */
  private draftPatientIdForUnknown: number | null = null;

  showNavigatePauseWarning = false;
  showPauseForLaterDialog = false;
  /** Resume failed: no local draft — offer delete of the server paused row. */
  orphanedPausedResume: {
    transcriptionId: string;
    originalFileName: string;
    patientDisplay: string;
  } | null = null;
  orphanPausedDeleteBusy = false;
  /** Looking up server checkpoint batches before showing the missing-draft banner. */
  restoringPausedFromServer = false;
  /** Pause was confirmed. The visit row is being saved; the last audio slice uploads after leaving this page. */
  savingPausedVisit = false;
  showDeleteOrphanPausedConfirmDialog = false;

  get orphanResumeBlocksRecording(): boolean {
    return this.orphanedPausedResume != null || this.restoringPausedFromServer;
  }
  /** After Stop with &lt; {@link MIN_RECORDING_SECONDS_FOR_UPLOAD}s captured, offer resume or discard. */
  showShortRecordingModal = false;
  /** Next mic start reuses in-memory chunks and checkpoint session (after short-recording modal Resume). */
  private continuingSameTakeAfterShortWarning = false;
  /**
   * True after Stop opened the short-recording modal until a new mic session starts successfully.
   * Unlike {@link #continuingSameTakeAfterShortWarning}, this survives {@link #onStartRecording}'s {@code finally}
   * so the main append/resume "Resume" button still continues the same take (baseline + chunks preserved).
   */
  private shortRecordingAwaitingContinue = false;
  /** Skip short-recording modal when tearing down during destroy. */
  private destroyingComponent = false;
  private inShortRecordingContinueFlow(): boolean {
    return this.continuingSameTakeAfterShortWarning || this.shortRecordingAwaitingContinue;
  }
  pauseModalPatientName = '';
  pauseModalPatientSuggestions: AutocompletePatientSummary[] = [];
  private pauseModalOpenedFrom: 'control' | 'navigate' | 'cancel' | null = null;
  private routeLeaveResolver: Subject<boolean> | null = null;
  /** After confirm pause: go to list vs stay and reset for a new capture. */
  private postPauseNavigateTarget: 'transcriptions' | 'freshRecording' | null = null;
  /** Inaudible output so iOS treats the page as playing audio and keeps the mic running in the background. */
  private keepAliveOsc: OscillatorNode | null = null;
  /** Mime type for this capture (MediaRecorder may be cleared before async persist finishes). */
  private activeRecordingMimeType = 'audio/webm;codecs=opus';
  
  // Real-time audio visualization
  audioLevel: number = 0;
  audioAnalyser: AnalyserNode | null = null;
  audioContext: AudioContext | null = null;
  /** Peak level seen during the first 20 seconds — used to warn when mic input is missing. */
  private peakAudioLevelSample = 0;
  private noAudioInputCheckTimer: ReturnType<typeof setTimeout> | null = null;
  private noAudioInputWarningShown = false;
  /** Prevents duplicate router.navigate when finalize/upload both signal processing. */
  private processingNavigateTriggered = false;

  /** Keeps the display awake while recording so Chrome/OS do not mute the mic when the screen sleeps. */
  private screenWakeLock: WakeLockSentinel | null = null;
  /** Bumps when mic start fails or recording stops so a late wake-lock promise does not leave a stray lock. */
  private screenWakeLockEpoch = 0;

  constructor(
    private messageService: MessageService,
    private recordingService: RecordingService,
    private patientsService: PatientsService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private appRef: ApplicationRef,
    private router: Router,
    private route: ActivatedRoute,
    private transcriptionsService: TranscriptionsService,
    private authService: AuthService,
    private recordingSession: RecordingSessionService
  ) {}

  /** True when this instance is mounted in the new left-rail shell (`/recording`), not `/v0/recording`. */
  @HostBinding('class.recording--shell')
  get inNewShell(): boolean {
    return !this.router.url.split('?')[0].startsWith('/v0');
  }

  private listAfterRecordingPath(): string[] {
    return this.inNewShell ? ['/'] : ['/v0/transcriptions'];
  }

  private homeAfterRecordingPath(): string[] {
    return this.inNewShell ? ['/'] : ['/v0/dashboard'];
  }

  private freshRecordingPath(): string[] {
    return this.inNewShell ? ['/recording'] : ['/v0/recording'];
  }

  private navigateToList(): void {
    void this.router.navigate(this.listAfterRecordingPath());
  }

  private navigateHome(): void {
    void this.router.navigate(this.homeAfterRecordingPath());
  }

  private replaceWithFreshRecording(): void {
    void this.router.navigate(this.freshRecordingPath(), { replaceUrl: true });
  }

  ngOnInit(): void {
    this.silentRecorderTeardownDone = false;
    void warmUpRecordingChunkStorage();
    this.authService.refreshSessionIfLoggedIn().subscribe();
    this.loadPatientSummariesFromResolver();
    if (this.allPatients.length === 0) {
      this.loadPatientSummaries().subscribe();
    }
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('visibilitychange', this.onVisibilityChangeForWakeLock);
    this.preferredMicrophoneId = this.readSavedMicrophoneId();
    void this.refreshMicrophones();
    navigator.mediaDevices?.addEventListener('devicechange', this.onAudioDevicesChanged);

    this.route.queryParamMap.pipe(take(1)).subscribe((q) => {
      const appendId = q.get('appendTranscription');
      if (appendId?.trim()) {
        this.loadAppendRecordingSession(appendId.trim());
        return;
      }
      const resumeId = q.get('resumeTranscription');
      if (resumeId?.trim()) {
        this.loadPausedResumeDraft(resumeId.trim());
        return;
      }
      const patientIdRaw = q.get('patientId');
      const patientId = patientIdRaw != null ? Number(patientIdRaw) : NaN;
      const autoStart = q.get('autoStart') === '1' || q.get('autoStart') === 'true';
      if (Number.isFinite(patientId) && patientId > 0) {
        this.applyPatientIdFromQuery(patientId, autoStart);
        return;
      }
      if (autoStart) {
        this.startUnknownPatientAutoRecording();
      }
    });

    this.route.queryParamMap.pipe(skip(1), takeUntil(this.destroy$)).subscribe((q) => {
      const autoStart = q.get('autoStart') === '1' || q.get('autoStart') === 'true';
      if (!autoStart) {
        return;
      }
      const patientIdRaw = q.get('patientId');
      const patientId = patientIdRaw != null ? Number(patientIdRaw) : NaN;
      if (Number.isFinite(patientId) && patientId > 0) {
        this.applyPatientIdFromQuery(patientId, true);
        return;
      }
      this.startUnknownPatientAutoRecording();
    });

    this.recordingSession.newRecordingFromNav$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.isRecording && !this.isPaused) {
        return;
      }
      if (this.isAppendVisitSoapNoPause) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Recording in progress',
          detail: 'Tap Stop to finish this addition before starting another recording.'
        });
        return;
      }
      this.postPauseNavigateTarget = 'freshRecording';
      this.showNavigatePauseWarning = true;
      this.pauseModalOpenedFrom = null;
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.destroyingComponent = true;
    this.abandonResumedPausedTakeOnNextStop = false;
    this.abandonResumedPausedNavigateAfter = null;
    this.blockRecorderAbandonLocalChunkPersist = false;
    this.shortRecordingAwaitingContinue = false;
    if (!this.silentRecorderTeardownDone) {
      this.stopRecording();
    }
    // The last Safari slice arrives after stop(). Killing the tracks here drops it.
    if (!this.processingHandoff) {
      this.cleanupAudio();
    }
    void this.releaseScreenWakeLock();
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('visibilitychange', this.onVisibilityChangeForWakeLock);
    navigator.mediaDevices?.removeEventListener('devicechange', this.onAudioDevicesChanged);
    this.recordingSession.setCaptureActive(false);
  }

  private onVisibilityChangeForWakeLock = (): void => {
    if (!this.isRecording) {
      return;
    }
    if (this.audioContext?.state === 'suspended') {
      void this.audioContext.resume();
    }
    if (document.visibilityState === 'visible') {
      void this.reacquireScreenWakeLockAfterVisible();
      void this.restoreCaptureAfterForeground();
    }
  };

  /**
   * Call `navigator.wakeLock.request()` synchronously from a click/tap handler (no await before it).
   * Safari (especially iOS) ties wake lock to user activation; awaiting getUserMedia first often breaks that.
   */
  private requestScreenWakeLockFromUserGesture(epoch: number): void {
    const nav = navigator as Navigator & { wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> } };
    if (!nav.wakeLock) {
      return;
    }
    try {
      nav.wakeLock
        .request('screen')
        .then((lock) => {
          if (epoch !== this.screenWakeLockEpoch) {
            void lock.release();
            return;
          }
          if (this.screenWakeLock && !this.screenWakeLock.released) {
            void lock.release();
            return;
          }
          this.screenWakeLock = lock;
          lock.addEventListener('release', () => {
            if (this.screenWakeLock === lock) {
              this.screenWakeLock = null;
            }
          });
        })
        .catch(() => {
          // Denied, unsupported context, or policy — recording still runs without wake lock.
        });
    } catch {
      // Synchronous throw from request() in some environments
    }
  }

  /** After tab focus returns, Chromium releases the lock; Safari may or may not allow re-request without a new tap. */
  private async reacquireScreenWakeLockAfterVisible(): Promise<void> {
    const nav = navigator as Navigator & { wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> } };
    if (!nav.wakeLock) {
      return;
    }
    try {
      if (this.screenWakeLock && !this.screenWakeLock.released) {
        return;
      }
      this.screenWakeLock = await nav.wakeLock.request('screen');
      this.screenWakeLock.addEventListener('release', () => {
        this.screenWakeLock = null;
      });
    } catch {
      // No gesture / policy — user can tap pause/resume or stop and start if needed.
    }
  }

  private async releaseScreenWakeLock(): Promise<void> {
    try {
      await this.screenWakeLock?.release();
    } catch {
      // ignore
    }
    this.screenWakeLock = null;
  }

  private handleClickOutside = (event: Event): void => {
    const target = event.target as HTMLElement;
    if (!target.closest('.autocomplete-container')) {
      this.showPatientDropdown = false;
    }
    if (this.microphoneMenuOpen && !target.closest('.mic-picker')) {
      this.microphoneMenuOpen = false;
      this.cdr.detectChanges();
    }
  }

  get activeMicrophoneId(): string {
    if (
      this.preferredMicrophoneId &&
      this.microphones.some((mic) => mic.deviceId === this.preferredMicrophoneId)
    ) {
      return this.preferredMicrophoneId;
    }
    return (
      this.microphones.find((mic) => mic.deviceId === 'default')?.deviceId ??
      this.microphones[0]?.deviceId ??
      ''
    );
  }

  get selectedMicrophoneLabel(): string {
    if (this.microphoneSwitching) {
      return 'Switching microphone…';
    }
    const active = this.microphones.find((mic) => mic.deviceId === this.activeMicrophoneId);
    return active?.label || 'Select microphone';
  }

  closeMicrophoneMenu(): void {
    this.microphoneMenuOpen = false;
  }

  async toggleMicrophoneMenu(event: Event): Promise<void> {
    event.stopPropagation();
    if (this.microphoneSwitching || this.isTranscribing || this.savingPausedVisit) {
      return;
    }
    if (this.microphoneMenuOpen) {
      this.microphoneMenuOpen = false;
      return;
    }
    if (!this.microphoneLabelsReady && !this.audioStream) {
      await this.promptForMicrophoneLabels();
    } else {
      await this.refreshMicrophones();
    }
    this.microphoneMenuOpen = true;
    this.cdr.detectChanges();
  }

  async selectMicrophone(deviceId: string): Promise<void> {
    if (this.microphoneSwitching || this.isTranscribing || !deviceId) {
      return;
    }
    this.microphoneMenuOpen = false;
    // "Default" clears the pin so a later headset change follows the OS.
    const nextId = deviceId === 'default' ? null : deviceId;
    const alreadyUsing =
      nextId === this.preferredMicrophoneId ||
      (this.preferredMicrophoneId == null && deviceId === this.activeMicrophoneId);
    if (alreadyUsing) {
      this.cdr.detectChanges();
      return;
    }
    const previous = this.preferredMicrophoneId;
    this.preferredMicrophoneId = nextId;
    this.writeSavedMicrophoneId(nextId);
    this.cdr.detectChanges();

    if (!this.isRecording && !this.isPaused) {
      return;
    }
    if (this.isStoppingRecording) {
      return;
    }

    this.microphoneSwitching = true;
    this.cdr.detectChanges();
    const keepPaused = this.isPaused;
    const result = await this.restartLiveCapture({ keepPaused, allowDeviceFallback: false });
    this.microphoneSwitching = false;
    if (result === 'unavailable') {
      this.preferredMicrophoneId = previous;
      this.writeSavedMicrophoneId(previous);
      this.microphoneRetargetPending = false;
      this.messageService.add({
        severity: 'warn',
        summary: 'Could not switch microphone',
        detail: 'The recording is still using the previous microphone.',
      });
    } else if (result === 'skipped') {
      this.microphoneRetargetPending = this.isRecording || this.isPaused;
    } else {
      this.microphoneRetargetPending = false;
    }
    this.cdr.detectChanges();
  }

  private onAudioDevicesChanged = (): void => {
    this.ngZone.run(() => {
      void this.refreshMicrophones();
    });
  };

  private async promptForMicrophoneLabels(): Promise<void> {
    try {
      const stream = await this.openMicrophoneStream(true);
      stream.getTracks().forEach((track) => track.stop());
    } catch {
      this.messageService.add({
        severity: 'warn',
        summary: 'Microphone permission needed',
        detail: 'Allow microphone access to see and choose input devices.',
      });
    }
    await this.refreshMicrophones();
  }

  private async refreshMicrophones(): Promise<void> {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return;
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter(
        (device) => device.kind === 'audioinput' && device.deviceId && device.deviceId !== 'communications'
      );
      this.microphoneLabelsReady = inputs.some((device) => !!device.label?.trim());
      let unnamed = 0;
      const choices: MicrophoneChoice[] = inputs.map((device) => {
        const label = device.label?.trim();
        if (label) {
          return { deviceId: device.deviceId, label };
        }
        if (device.deviceId === 'default') {
          return { deviceId: device.deviceId, label: 'Default microphone' };
        }
        unnamed += 1;
        return { deviceId: device.deviceId, label: `Microphone ${unnamed}` };
      });
      choices.sort((a, b) => {
        if (a.deviceId === 'default') return 1;
        if (b.deviceId === 'default') return -1;
        return a.label.localeCompare(b.label);
      });
      this.microphones = choices;
      if (this.preferredMicrophoneId && !choices.some((mic) => mic.deviceId === this.preferredMicrophoneId)) {
        // The pinned device was unplugged. Follow the system default until the user picks again.
        if (!this.isRecording && !this.isPaused) {
          this.preferredMicrophoneId = null;
          this.writeSavedMicrophoneId(null);
        }
      }
    } catch {
      // Leave the previous list in place.
    }
    this.cdr.detectChanges();
  }

  private readSavedMicrophoneId(): string | null {
    try {
      const saved = localStorage.getItem(AUDIO_INPUT_PREF_KEY)?.trim();
      return saved || null;
    } catch {
      return null;
    }
  }

  private writeSavedMicrophoneId(deviceId: string | null): void {
    try {
      if (!deviceId) {
        localStorage.removeItem(AUDIO_INPUT_PREF_KEY);
        return;
      }
      localStorage.setItem(AUDIO_INPUT_PREF_KEY, deviceId);
    } catch {
      // Private mode or a full store — the choice still applies to this visit.
    }
  }

  /** Called by p-autoComplete on focus and when user types – loads from backend if needed, then filters */
  onCompletePatientSearch(event: { query: string }): void {
    const query = ((event?.query) ?? '').toString().toLowerCase().trim();
    const applyFilter = () => {
      if (query.length >= 1) {
        this.filteredPatients = this.allPatients.filter(patient => {
          const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
          const mrn = (patient.mrn ?? '').toLowerCase();
          return fullName.includes(query) || mrn.includes(query);
        });
      } else {
        this.filteredPatients = this.allPatients.slice(0, 10);
      }
      this.cdr.detectChanges();
    };
    if (this.allPatients.length === 0) {
      this.loadPatientSummaries().subscribe({
        next: () => applyFilter(),
        error: () => {
          this.filteredPatients = [];
          this.cdr.detectChanges();
        }
      });
    } else {
      applyFilter();
    }
  }

  onPatientSelectFromDropdown(patient: PatientSummary | null): void {
    if (patient) this.selectPatient(patient);
  }

  onPauseModalPatientSearch(event: { query: string }): void {
    const query = ((event?.query) ?? '').toString().toLowerCase().trim();
    const applyFilter = () => {
      if (query.length >= 1) {
        this.pauseModalPatientSuggestions = this.allPatients.filter((patient) => {
          const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
          const mrn = (patient.mrn ?? '').toLowerCase();
          return fullName.includes(query) || mrn.includes(query);
        });
      } else {
        this.pauseModalPatientSuggestions = this.allPatients.slice(0, 10);
      }
      this.cdr.detectChanges();
    };
    if (this.allPatients.length === 0) {
      this.loadPatientSummaries().subscribe({
        next: () => applyFilter(),
        error: () => {
          this.pauseModalPatientSuggestions = [];
          this.cdr.detectChanges();
        }
      });
    } else {
      applyFilter();
    }
  }

  onPauseModalPatientSelect(patient: PatientSummary | null): void {
    if (!patient) {
      return;
    }
    this.selectPatient(patient);
    this.pauseModalPatientName = `${patient.firstName} ${patient.lastName}`.trim();
  }

  onPauseModalPatientNameChange(): void {
    if (!this.selectedPatient) {
      return;
    }
    const selectedName = `${this.selectedPatient.firstName} ${this.selectedPatient.lastName}`.trim().toLowerCase();
    const typed = (this.pauseModalPatientName ?? '').trim().toLowerCase();
    if (typed !== selectedName) {
      this.selectedPatient = null;
      this.patientInfo.mrn = '';
      this.isMrnReadonly = false;
    }
  }



  loadPatientSummariesFromResolver(): void {
    // Get the resolved data from the route
    const resolvedData = this.route.snapshot.data['patientSummaries'] as PatientSummary[];
    
    if (resolvedData && resolvedData.length > 0) {
      this.allPatients = resolvedData.map((p) => ({
        ...p,
        displayName: p.displayName ?? `${p.firstName} ${p.lastName}`.trim(),
        nameOnly: `${p.firstName} ${p.lastName}`.trim()
      }));
      this.filteredPatients = this.allPatients.slice(0, 10); // Show first 10 initially
    } else {
      this.allPatients = [];
      this.filteredPatients = [];
    }
  }

  loadPatientSummaries(): Observable<AutocompletePatientSummary[]> {
    return this.patientsService.getPatientSummaries().pipe(
      map((patients: PatientSummary[]) =>
        patients.map(p => ({
          ...p,
          displayName: p.displayName ?? `${p.firstName} ${p.lastName}`.trim(),
          nameOnly: `${p.firstName} ${p.lastName}`.trim()
        }))
      ),
      tap((patients) => {
        this.allPatients = patients;
        this.filteredPatients = this.allPatients.slice(0, 10);
      })
    );
  }

  /**
   * Create a patient from the typed search name (no modal) and start recording.
   * Extra demographics can be filled in later from Patients.
   */
  createPatientFromNameAndStart(): void {
    if (
      this.creatingPatientFromName ||
      this.isRecording ||
      this.isTranscribing ||
      this.isStoppingRecording ||
      this.orphanResumeBlocksRecording
    ) {
      return;
    }
    const typed = this.selectedPatientDisplay;
    const name =
      typeof typed === 'string'
        ? typed.trim()
        : `${(typed as AutocompletePatientSummary | null)?.firstName ?? ''} ${(typed as AutocompletePatientSummary | null)?.lastName ?? ''}`.trim() ||
          ((typed as AutocompletePatientSummary | null)?.displayName ?? '').trim();
    if (!name) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Patient name needed',
        detail: 'Type a name in the search field, then click Create New Patient.'
      });
      return;
    }
    if (this.isUnknownDisplayName(name)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Enter a real name',
        detail: 'Use a patient name so the visit can be filed correctly.'
      });
      return;
    }

    this.creatingPatientFromName = true;
    const parts = name.split(/\s+/).filter(Boolean);
    const firstName = parts[0] ?? name;
    const lastName = parts.slice(1).join(' ') || firstName;

    this.patientsService.createPatient({ firstName, lastName }).subscribe({
      next: (patient: PatientResponse) => {
        this.applyCreatedPatient(patient);
        this.creatingPatientFromName = false;
        this.cdr.detectChanges();
        void this.onStartRecording();
      },
      error: (err) => {
        this.creatingPatientFromName = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Could not create patient',
          detail: err?.error?.message ?? err?.message ?? 'Try again.'
        });
        this.cdr.detectChanges();
      }
    });
  }

  private applyCreatedPatient(patient: PatientResponse): void {
    const newPatientSummary: AutocompletePatientSummary = {
      id: patient.id,
      firstName: patient.firstName,
      lastName: patient.lastName,
      mrn: patient.mrn,
      displayName: `${patient.firstName} ${patient.lastName}`.trim(),
      nameOnly: `${patient.firstName} ${patient.lastName}`.trim()
    };
    this.selectPatient(newPatientSummary);
    if (!this.allPatients.some((p) => p.id === patient.id)) {
      this.allPatients = [newPatientSummary, ...this.allPatients];
    }
    this.visitPauseNameLocked = true;
    void this.loadPatientSummaries().subscribe({ error: () => undefined });
  }

  // Patient search methods
  searchPatients(event: any): void {
    const query = event.query.toLowerCase();
    if (query && query.length >= 1) {
      // Filter from pre-loaded patients for faster search
      this.filteredPatients = this.allPatients.filter(patient => {
        const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
        const mrn = patient.mrn.toLowerCase();
        return fullName.includes(query) || mrn.includes(query);
      });
    } else {
      this.filteredPatients = this.allPatients.slice(0, 10); // Show first 10 patients
    }
  }

  onPatientSelect(event: any): void {
    this.selectedPatient = event;
    if (this.selectedPatient) {
      this.selectedPatientDisplay = `${this.selectedPatient.firstName} ${this.selectedPatient.lastName}`;
      this.patientInfo.patientName = `${this.selectedPatient.firstName} ${this.selectedPatient.lastName}`;
      this.patientInfo.mrn = this.selectedPatient.mrn;
      this.isMrnReadonly = true;
    }
  }

  onPatientClear(): void {
    this.selectedPatient = null;
    this.selectedPatientDisplay = '';
    this.patientInfo.patientName = '';
    this.patientInfo.mrn = '';
    this.isMrnReadonly = false;
    this.filteredPatients = this.allPatients.slice(0, 10);
  }

  // Method to get the display value for the autocomplete
  getPatientDisplayValue(): string {
    if (this.selectedPatient) {
      return `${this.selectedPatient.firstName} ${this.selectedPatient.lastName}`;
    }
    return this.patientInfo.patientName || '';
  }

  /** Label under the title in the new shell. A pencil opens the same rename/search used on a visit. */
  get recordingPatientLabel(): string {
    const fromSelected = this.getPatientDisplayValue().trim();
    if (fromSelected) {
      return fromSelected;
    }
    return typeof this.selectedPatientDisplay === 'string' ? this.selectedPatientDisplay.trim() : '';
  }

  get isRecordingPatientUnknown(): boolean {
    return this.isUnknownDisplayName(this.recordingPatientLabel);
  }

  editingRecordingPatient = false;
  recordingPatientNameDraft = '';
  recordingPatientNameSaving = false;
  recordingPatientNameError: string | null = null;
  recordingNameDropdownOpen = false;
  recordingNameHighlight = 0;
  recordingNameSuggestions: AutocompletePatientSummary[] = [];
  private recordingNameSelectedPatient: PatientSummary | null = null;

  @ViewChild('recordingPatientInput') recordingPatientInput?: ElementRef<HTMLInputElement>;

  startRecordingPatientEdit(): void {
    if (this.recordingPatientNameSaving) {
      return;
    }
    const current = this.recordingPatientLabel;
    this.recordingPatientNameDraft = this.isUnknownDisplayName(current) ? '' : current;
    this.recordingNameSelectedPatient = this.selectedPatient;
    this.recordingNameSuggestions = [];
    this.recordingNameDropdownOpen = false;
    this.recordingNameHighlight = 0;
    this.recordingPatientNameError = null;
    this.editingRecordingPatient = true;
    this.cdr.detectChanges();
    setTimeout(() => this.recordingPatientInput?.nativeElement.focus(), 0);
  }

  cancelRecordingPatientEdit(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.recordingPatientNameSaving) {
      return;
    }
    this.editingRecordingPatient = false;
    this.recordingNameDropdownOpen = false;
    this.recordingPatientNameError = null;
    this.recordingNameSelectedPatient = null;
  }

  onRecordingPatientNameDraftChange(value: string): void {
    this.recordingPatientNameDraft = value;
    this.recordingPatientNameError = null;
    const query = value.trim().toLowerCase();
    if (this.recordingNameSelectedPatient) {
      const selectedName = `${this.recordingNameSelectedPatient.firstName} ${this.recordingNameSelectedPatient.lastName}`
        .trim()
        .toLowerCase();
      if (query !== selectedName) {
        this.recordingNameSelectedPatient = null;
      }
    }
    if (!query) {
      this.recordingNameSuggestions = [];
      this.recordingNameDropdownOpen = false;
      return;
    }
    this.recordingNameSuggestions = this.allPatients
      .filter((patient) => {
        const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
        const mrn = (patient.mrn ?? '').toLowerCase();
        return fullName.includes(query) || mrn.includes(query);
      })
      .slice(0, 8);
    this.recordingNameDropdownOpen = this.recordingNameSuggestions.length > 0;
    this.recordingNameHighlight = 0;
  }

  onRecordingPatientNameKeydown(event: KeyboardEvent): void {
    const total = this.recordingNameSuggestions.length;
    const canMove = this.recordingPatientNameDraft.trim().length > 0 && total > 0;
    if (event.key === 'ArrowDown' && canMove) {
      event.preventDefault();
      this.recordingNameDropdownOpen = true;
      this.recordingNameHighlight = (this.recordingNameHighlight + 1) % total;
      return;
    }
    if (event.key === 'ArrowUp' && canMove) {
      event.preventDefault();
      this.recordingNameDropdownOpen = true;
      this.recordingNameHighlight = (this.recordingNameHighlight - 1 + total) % total;
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveRecordingPatientName();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.recordingNameDropdownOpen) {
        this.recordingNameDropdownOpen = false;
        return;
      }
      this.cancelRecordingPatientEdit();
    }
  }

  selectRecordingPatientSuggestion(patient: PatientSummary): void {
    this.recordingNameSelectedPatient = patient;
    this.recordingPatientNameDraft = `${patient.firstName} ${patient.lastName}`.trim();
    this.recordingNameDropdownOpen = false;
    this.recordingPatientNameError = null;
    this.saveRecordingPatientName();
  }

  saveRecordingPatientName(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.editingRecordingPatient || this.recordingPatientNameSaving) {
      return;
    }
    const patientName = this.recordingPatientNameDraft.trim();
    if (!patientName) {
      this.recordingPatientNameError = 'Enter a patient name.';
      return;
    }
    let patient = this.recordingNameSelectedPatient;
    if (!patient) {
      const typed = patientName.toLowerCase();
      patient =
        this.allPatients.find(
          (p) => `${p.firstName} ${p.lastName}`.trim().toLowerCase() === typed
        ) ?? null;
    }
    const transcriptionId = this.resumeTranscriptionDbId ?? this.appendTranscriptionDbId;
    if (transcriptionId != null) {
      this.recordingPatientNameSaving = true;
      this.recordingPatientNameError = null;
      this.transcriptionsService
        .updateTranscriptionMetadata(String(transcriptionId), {
          patientName,
          appointmentType: this.patientInfo.appointmentType?.trim() || this.defaultAppointmentType,
          patientId: patient?.id,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (detail) => {
            const savedName = (detail?.patientName ?? patientName).trim();
            this.applyRecordingPatientChoice(savedName, patient);
            if (detail?.mrn) {
              this.patientInfo.mrn = detail.mrn;
            }
            if (detail?.patientId != null) {
              this.draftPatientIdForUnknown = detail.patientId;
            }
            this.recordingPatientNameSaving = false;
            this.editingRecordingPatient = false;
            this.recordingNameDropdownOpen = false;
            this.cdr.detectChanges();
          },
          error: () => {
            this.recordingPatientNameSaving = false;
            this.recordingPatientNameError = 'Could not update patient name. Try again.';
            this.cdr.detectChanges();
          },
        });
      return;
    }
    this.applyRecordingPatientChoice(patientName, patient);
    this.editingRecordingPatient = false;
    this.recordingNameDropdownOpen = false;
  }

  /** Keep the in-progress visit pointed at the chosen chart name until pause or stop. */
  private applyRecordingPatientChoice(name: string, patient: PatientSummary | null): void {
    if (patient) {
      this.selectPatient(patient);
      this.draftPatientIdForUnknown = patient.id;
      this.visitPauseNameLocked = true;
      return;
    }
    this.selectedPatient = null;
    this.selectedPatientDisplay = name;
    this.patientInfo.patientName = name;
    this.patientInfo.mrn = '';
    this.isMrnReadonly = false;
    this.draftPatientIdForUnknown = null;
    this.visitPauseNameLocked = !this.isUnknownDisplayName(name);
  }

  onPatientInput(event: any): void {
    const query = (event.target.value ?? '').toString().toLowerCase().trim();
    this.showPatientDropdown = true;
    if (query.length >= 1) {
      this.filteredPatients = this.allPatients.filter(patient => {
        const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
        const mrn = (patient.mrn ?? '').toLowerCase();
        return fullName.includes(query) || mrn.includes(query);
      });
    } else {
      this.filteredPatients = this.allPatients.length ? this.allPatients.slice(0, 10) : [];
    }
    this.cdr.detectChanges();
  }

  selectPatient(patient: PatientSummary): void {
    this.selectedPatient = patient;
    this.selectedPatientDisplay = `${patient.firstName} ${patient.lastName}`;
    this.patientInfo.patientName = `${patient.firstName} ${patient.lastName}`;
    this.patientInfo.mrn = patient.mrn ?? '';
    this.isMrnReadonly = true;
    this.showPatientDropdown = false;
  }

  private startUnknownPatientAutoRecording(): void {
    if (this.isRecording || this.isPaused || this.isTranscribing || this.orphanResumeBlocksRecording) {
      return;
    }
    this.selectedPatient = null;
    this.selectedPatientDisplay = this.defaultPatientName;
    this.patientInfo.patientName = this.defaultPatientName;
    this.patientInfo.mrn = '';
    this.isMrnReadonly = false;
    this.visitPauseNameLocked = false;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { autoStart: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    void this.beginAutoStartRecordingAfterQueryLoad();
  }

  private applyPatientIdFromQuery(patientId: number, autoStart = false): void {
    const clearPatientQuery = () => {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { patientId: null, autoStart: null },
        replaceUrl: true,
      });
    };
    const lockPatientAndClearQuery = () => {
      this.visitPauseNameLocked = true;
      this.cdr.detectChanges();
      clearPatientQuery();
    };
    // Start the mic in this turn when requested — do not wait on a patient lookup.
    if (autoStart) {
      void this.beginAutoStartRecordingAfterQueryLoad();
    }
    const fromList = this.allPatients.find((p) => Number(p.id) === patientId);
    if (fromList) {
      this.selectPatient(fromList);
      lockPatientAndClearQuery();
      return;
    }
    this.patientsService.getPatientById(patientId).subscribe({
      next: (patient) => {
        const name = `${patient.firstName} ${patient.lastName}`.trim();
        const summary: AutocompletePatientSummary = {
          id: patient.id,
          firstName: patient.firstName,
          lastName: patient.lastName,
          mrn: patient.mrn,
          displayName: name,
          nameOnly: name,
        };
        if (!this.allPatients.some((p) => Number(p.id) === summary.id)) {
          this.allPatients = [summary, ...this.allPatients];
        }
        this.selectPatient(summary);
        lockPatientAndClearQuery();
      },
      error: () => {
        /* Landing may have passed a stale id; leave picker empty. */
        clearPatientQuery();
      },
    });
  }

  async onStartRecording(): Promise<void> {
    if (this.isRecording || this.isTranscribing || this.orphanResumeBlocksRecording) {
      return;
    }
    if (this.serverCheckpointContainerMismatch) {
      this.messageService.add({
        severity: 'info',
        summary: 'Saved audio is on the server',
        detail:
          'This browser records in a different format, so it can’t add more to this take. End the visit to transcribe the audio already saved.',
      });
      return;
    }
    this.processingNavigateTriggered = false;
    this.silentRecorderTeardownDone = false;
    this.hasAttemptedRecording = true;
    const preserveShortTake = this.inShortRecordingContinueFlow();
    // After the first Record on a list-resumed paused visit, pendingResumeRecordingUuid is cleared.
    // Do not treat that as a "new visit" or we wipe resumeTranscriptionDbId and the accumulated timer.
    const continuedPausedVisit =
      this.resumeTranscriptionDbId != null && this.appendTranscriptionDbId == null;
    if (!preserveShortTake && !this.pendingResumeRecordingUuid && !continuedPausedVisit) {
      if (!this.appendTranscriptionDbId) {
        this.visitPauseNameLocked = false;
        this.draftPatientIdForUnknown = null;
        this.resumeTranscriptionDbId = null;
        this.totalRecordingTime = 0;
        this.recordingTime = '00:00';
        this.updateTimerDisplay();
      }
    }
    const enteredPatientName = this.selectedPatientDisplay?.trim() ?? '';
    const hasSelectedPatient = !!this.selectedPatient;

    if (!hasSelectedPatient) {
      this.patientInfo.patientName = enteredPatientName || this.defaultPatientName;
      // Keep MRN when continuing a paused draft so stop/finalize does not create a duplicate patient.
      if (!continuedPausedVisit && this.draftPatientIdForUnknown == null && !this.patientInfo.mrn?.trim()) {
        this.patientInfo.mrn = '';
      }
    }

    this.patientInfo.appointmentType =
      this.patientInfo.appointmentType?.trim() || this.defaultAppointmentType;
    this.patientInfo.neurologicalSpecialty =
      this.patientInfo.neurologicalSpecialty?.trim() || this.defaultNeurologicalSpecialty;

    try {
      // Set recording state immediately for UI responsiveness
      this.isRecording = true;
      this.isPaused = false;
      
      // Force change detection to update UI immediately
      this.cdr.detectChanges();

      const wakeEpoch = ++this.screenWakeLockEpoch;
      // Must run in same synchronous turn as the click (before await) for Safari user-activation rules.
      this.requestScreenWakeLockFromUserGesture(wakeEpoch);
      
      await this.startAudioRecording();
      this.shortRecordingAwaitingContinue = false;
      this.pendingResumeRecordingUuid = null;
      this.recordingStartTime = Date.now();
      this.startTimer();
      this.syncCaptureActive();
    } catch (error) {
      this.screenWakeLockEpoch++;
      void this.releaseScreenWakeLock();
      this.shortRecordingAwaitingContinue = false;
      // Reset state if recording fails
      this.isRecording = false;
      this.isPaused = false;
      this.cdr.detectChanges();
      
      this.messageService.add({
        severity: 'error',
        summary: 'Recording Failed',
        detail:
          error instanceof Error && error.message
            ? error.message
            : 'Failed to start recording. Check Settings → Scryber → Microphone is allowed.',
      });
    } finally {
      this.continuingSameTakeAfterShortWarning = false;
    }
  }

  onPauseRecording(): void {
    if (!this.isRecording || this.orphanResumeBlocksRecording) {
      return;
    }
    if (this.isAppendVisitSoapNoPause) {
      return;
    }
    if (!this.isPaused) {
      this.postPauseNavigateTarget = 'transcriptions';
      this.pauseRecording();
      this.pauseModalOpenedFrom = 'control';
      // Append (add to completed visit): allow mic pause/resume only. Pause-for-later would need a separate draft model;
      // do not auto-open save-draft or it hits the append-only guard in finishPauseForLater.
      const appendOnlyMicPause =
        this.appendTranscriptionDbId != null && this.resumeTranscriptionDbId == null;
      if (appendOnlyMicPause) {
        this.cdr.detectChanges();
        return;
      }
      if (this.shouldShowPausePatientDialog() && !this.isCompactShell()) {
        this.pauseModalPatientName = this.getPauseModalInitialName();
        this.pauseModalPatientSuggestions = this.allPatients.slice(0, 10);
        this.showPauseForLaterDialog = true;
        this.cdr.detectChanges();
      } else {
        // Phone layout matches desktop: pause returns to the visits list without naming the patient.
        void this.finishPauseForLater(!(this.isCompactShell() && this.shouldShowPausePatientDialog()));
      }
      return;
    }
    if (this.showPauseForLaterDialog || this.showNavigatePauseWarning) {
      return;
    }
    void this.resumeRecording();
  }

  onStopRecording(): void {
    if (this.orphanResumeBlocksRecording) {
      return;
    }
    this.stopRecording();
  }

  /**
   * From a loaded paused visit (timer shown, capture not started), finalize checkpoints and transcribe
   * without recording new audio.
   */
  /**
   * User opened “add more” to an existing visit but chose not to record — leave without merging.
   */
  onEndAppendVisitFromIdle(): void {
    if (!this.isAppendVisitIdle || this.isTranscribing || this.orphanResumeBlocksRecording) {
      return;
    }
    this.appendTranscriptionDbId = null;
    this.appendCanonicalRecordingUuid = null;
    this.appendVisitHasSoap = false;
    this.restorePreservedManifestAfterAppendIfNeeded();
    this.visitPauseNameLocked = false;
    this.resetTimer();
    this.messageService.add({
      severity: 'info',
      summary: 'No new audio',
      detail: this.inNewShell
        ? 'You can add more later from the visits list.'
        : 'You can add more later from the transcriptions list.'
    });
    this.navigateToList();
    this.cdr.detectChanges();
  }

  onEndResumedVisitFromIdle(): void {
    if (!this.isResumedVisitIdle || this.isTranscribing || this.orphanResumeBlocksRecording) {
      return;
    }
    const uuid = this.pendingResumeRecordingUuid!;
    const manifest = readRecordingCheckpoint();
    if (!manifest || manifest.recordingId !== uuid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot end visit',
        detail: 'Draft data is missing. Resume on the same browser where you paused.'
      });
      return;
    }
    const serverBatchesReady =
      typeof manifest.nextCheckpointBatchNumber === 'number' && manifest.nextCheckpointBatchNumber > 1;
    if (manifest.lastChunkIndex < 0 && !serverBatchesReady) {
      this.messageService.add({
        severity: 'info',
        summary: 'No audio yet',
        detail: 'Press Resume to record, or stay on this page to keep the paused visit.'
      });
      return;
    }
    this.recordingId = uuid;
    this.nextChunkIndex = manifest.lastChunkIndex + 1;
    this.nextCheckpointBatchNumber = this.inferNextCheckpointBatchNumberFromManifest(manifest);
    this.firstPendingUploadChunkIndex = firstChunkIndexPendingUpload(manifest, this.activeRecordingMimeType);
    this.anyCheckpointUploadedToServer = this.manifestIndicatesServerCheckpointBatches(manifest);
    this.activeRecordingMimeType = manifest.recorderMimeType || 'audio/webm;codecs=opus';
    this.audioChunks = [];
    if (!this.anyCheckpointUploadedToServer) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Audio not on server yet',
        detail:
          'No checkpoint batches were stored for this draft. Resume on this device, record briefly if needed, then tap Stop so audio can upload.'
      });
      return;
    }
    this.ngZone.run(() => this.transcribeAfterStop());
  }

  /**
   * Simulator builds talk to 127.0.0.1 and record through the Mac mic. Echo cancellation and noise
   * suppression treat a nearby phone speaker as echo and leave a silent track.
   */
  private microphoneConstraints(): MediaTrackConstraints {
    const base: MediaTrackConstraints = environment.apiUrl.includes('127.0.0.1')
      ? {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        }
      : shouldUseSafariMp4Recording()
        ? {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }
        : {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100,
          };
    const id = this.preferredMicrophoneId;
    if (!id) {
      return base;
    }
    // Headsets often reject a required 44100. Ask for it, but accept the device's rate.
    const selected: MediaTrackConstraints = { ...base, deviceId: { exact: id } };
    if (typeof base.sampleRate === 'number') {
      selected.sampleRate = { ideal: base.sampleRate };
    }
    return selected;
  }

  private async openMicrophoneStream(allowDeviceFallback = true): Promise<MediaStream> {
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: this.microphoneConstraints(),
      });
    } catch (err) {
      if (!allowDeviceFallback || !this.preferredMicrophoneId || !this.isMissingMicrophoneError(err)) {
        throw err;
      }
      this.preferredMicrophoneId = null;
      this.writeSavedMicrophoneId(null);
      this.messageService.add({
        severity: 'warn',
        summary: 'Microphone unavailable',
        detail: 'That microphone is disconnected. Using the system default.',
      });
      return navigator.mediaDevices.getUserMedia({
        audio: this.microphoneConstraints(),
      });
    }
  }

  private isMissingMicrophoneError(err: unknown): boolean {
    const name = err && typeof err === 'object' && 'name' in err ? String((err as { name: string }).name) : '';
    return name === 'OverconstrainedError' || name === 'NotFoundError';
  }

  private async startAudioRecording(): Promise<void> {
    try {
      const preserveTake = this.inShortRecordingContinueFlow();

      this.audioStream = await this.openMicrophoneStream();
      void this.refreshMicrophones();

      // Set up real-time audio analysis
      this.setupAudioAnalysis();

      // Pick a container this browser actually supports: WebM/Opus on Chrome/Android, MP4/AAC on iOS Safari/WebView.
      const recorderFormat = pickRecorderFormatForPlatform();
      this.mediaRecorder = recorderFormat.mimeType
        ? new MediaRecorder(this.audioStream, { mimeType: recorderFormat.mimeType })
        : new MediaRecorder(this.audioStream);
      // Read back the effective type (the browser fills it in when we pass no mimeType).
      this.activeRecordingMimeType =
        this.mediaRecorder.mimeType || recorderFormat.mimeType || 'audio/webm;codecs=opus';

      const timesliceMs = checkpointTimesliceMs(this.activeRecordingMimeType);

      if (!preserveTake) {
        this.audioChunks = [];
      }
      this.hasSavedCurrentRecording = false;
      this.isStoppingRecording = false;
      const resumeUuid = this.pendingResumeRecordingUuid;
      if (preserveTake) {
        if (!this.recordingId) {
          throw new Error('No active recording session to continue');
        }
        // Keep recordingId, audioChunks, nextChunkIndex, checkpoint batch state from before Stop.
      } else if (resumeUuid) {
        const manifest = readRecordingCheckpoint();
        if (!manifest || manifest.recordingId !== resumeUuid) {
          throw new Error('Resume manifest missing');
        }
        this.recordingId = resumeUuid;
        this.nextChunkIndex = manifest.lastChunkIndex + 1;
        this.nextCheckpointBatchNumber = this.inferNextCheckpointBatchNumberFromManifest(manifest);
        this.firstPendingUploadChunkIndex = firstChunkIndexPendingUpload(
          manifest,
          this.activeRecordingMimeType
        );
        this.audioChunksLogicalBase = this.nextChunkIndex;
        this.anyCheckpointUploadedToServer = this.manifestIndicatesServerCheckpointBatches(manifest);
        // Cancel after list resume must drop only slices from this mic session, not the paused draft on disk.
        this.manifestSnapshotBeforeResumedTake = {
          recordingId: manifest.recordingId,
          recordingStartedAt: manifest.recordingStartedAt,
          lastUploadAt: manifest.lastUploadAt,
          lastChunkIndex: manifest.lastChunkIndex,
          accumulatedDurationSeconds: manifest.accumulatedDurationSeconds,
          pausedTranscriptionDbId: manifest.pausedTranscriptionDbId,
          visitPatientNameLocked: manifest.visitPatientNameLocked,
          draftPatientId: manifest.draftPatientId,
          nextCheckpointBatchNumber: manifest.nextCheckpointBatchNumber
        };
      } else {
        const startedAt = Date.now();
        const reuseAppendUuid =
          this.appendTranscriptionDbId != null ? this.appendCanonicalRecordingUuid?.trim().toLowerCase() : null;
        if (reuseAppendUuid) {
          const currentManifest = readRecordingCheckpoint();
          if (
            currentManifest &&
            currentManifest.recordingId !== reuseAppendUuid &&
            !this.preservedManifestBeforeAppend
          ) {
            this.preservedManifestBeforeAppend = { ...currentManifest };
          }
        } else {
          releaseActiveCheckpointForNewVisit();
        }
        this.recordingId =
          reuseAppendUuid && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(reuseAppendUuid)
            ? reuseAppendUuid
            : createRecordingUuid();
        writeRecordingDraftStarted(this.recordingId, startedAt, this.activeRecordingMimeType);
        this.nextChunkIndex = 0;
        this.firstPendingUploadChunkIndex = 0;
        this.audioChunksLogicalBase = 0;
        this.nextCheckpointBatchNumber = 1;
        this.anyCheckpointUploadedToServer = false;
        this.manifestSnapshotBeforeResumedTake = null;
      }
      this.periodicFlushActive = true;
      this.pauseTailUpload = Promise.resolve(true);
      this.pauseTailPersisted = Promise.resolve();
      this.persistTail = Promise.resolve();
      this.checkpointBatchUploadInFlight = false;
      this.checkpointUploadGate = Promise.resolve();

      this.attachMediaRecorderHandlers(this.mediaRecorder);

      if (this.usesSafariSegmentedRecording()) {
        this.mediaRecorder.start();
        this.scheduleNextSafariSegment();
      } else {
        this.mediaRecorder.start(timesliceMs);
      }
      this.startRecordingKeepAlive();

      this.startNoAudioInputWatch();
      
    } catch (error) {
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        throw new Error(
          'Microphone is blocked on insecure HTTP. Rebuild for device: npm run ios:device'
        );
      }
      if (typeof navigator !== 'undefined' && !navigator.mediaDevices?.getUserMedia) {
        throw new Error('Microphone API is not available in this WebView');
      }
      throw new Error('Failed to access microphone');
    }
  }

  private setupAudioAnalysis(): void {
    if (!this.audioStream) return;
    
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      this.audioAnalyser = this.audioContext.createAnalyser();
      this.audioAnalyser.fftSize = 256;
      
      const source = this.audioContext.createMediaStreamSource(this.audioStream);
      source.connect(this.audioAnalyser);
      
      // Start real-time audio level monitoring
      this.monitorAudioLevel();
    } catch (error) {
      // Audio analysis not supported in this browser
    }
  }

  private monitorAudioLevel(): void {
    if (!this.audioAnalyser) return;
    
    const dataArray = new Uint8Array(this.audioAnalyser.frequencyBinCount);
    
    const updateLevel = () => {
      if (!this.audioAnalyser || !this.isRecording) return;
      
      this.audioAnalyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      this.audioLevel = average;
      this.peakAudioLevelSample = Math.max(this.peakAudioLevelSample, average);
      
      // Force change detection for real-time audio level updates
      this.cdr.detectChanges();
      
      // Continue monitoring if still recording
      if (this.isRecording) {
        requestAnimationFrame(updateLevel);
      }
    };
    
    updateLevel();
  }

  private usesSafariSegmentedRecording(): boolean {
    return usesFragmentedMp4Checkpoints(this.activeRecordingMimeType);
  }

  private attachMediaRecorderHandlers(recorder: MediaRecorder): void {
    recorder.ondataavailable = (event) => {
      if (this.suppressChunkPersistUntilStopHandled || this.blockRecorderAbandonLocalChunkPersist) {
        this.resolveSegmentDataIfAwaiting();
        return;
      }
      if (event.data.size > 0) {
        const chunkIndex = this.nextChunkIndex++;
        this.audioChunks.push({
          data: event.data,
          timestamp: Date.now()
        });
        if (this.recordingId) {
          this.queuePersistRecordingChunk(chunkIndex, event.data);
        }
        // Upload only after this slice is durably persisted — otherwise a successful POST can be followed by a
        // late IndexedDB put, leaving an orphan chunk and failing finalize on Stop.
        void this.persistTail.catch(() => undefined).then(() => {
          this.ngZone.run(() => this.trySchedulePeriodicCheckpointUpload());
        });
      }
      // Release the stop barrier's wait — this is the flush data for the most recent stop().
      this.resolveSegmentDataIfAwaiting();
      this.cdr.detectChanges();
    };

    recorder.onstop = () => {
      const restartingCapture = this.captureRestartRecorder === recorder;
      if (restartingCapture) {
        this.captureRestartRecorder = null;
      }
      const segmentRotation =
        !restartingCapture &&
        this.safariSegmentStopPending &&
        !this.isStoppingRecording &&
        !this.abandonResumedPausedTakeOnNextStop &&
        !this.abandonAppendAdditionOnNextStop &&
        !this.discardRecordingOnNextStop;
      this.safariSegmentStopPending = false;

      // WebKit may deliver ondataavailable after onstop — drain persist/upload before rotating or finalizing.
      void this.drainAfterRecorderStop().then(() => {
        this.releaseRecorderStopBarrier();

        if (restartingCapture) {
          const resolve = this.resolveCaptureRestart;
          this.resolveCaptureRestart = null;
          resolve?.();
          return;
        }

        if (segmentRotation) {
          this.ngZone.run(() => {
            if (
              this.periodicFlushActive &&
              this.audioStream &&
              !this.isPaused &&
              !this.isStoppingRecording
            ) {
              this.startSafariRecorderSegment();
            }
          });
          return;
        }

        // Final stop: release mic/stream after the last slice is persisted/uploaded.
        this.cleanupAudio();
        this.ngZone.run(() => {
        if (this.applyPendingAbandonAfterStop()) {
          return;
        }
        this.suppressChunkPersistUntilStopHandled = false;
        if (this.shouldOfferShortRecordingChoice()) {
          this.isStoppingRecording = false;
          this.shortRecordingAwaitingContinue = true;
          this.showShortRecordingModal = true;
          this.cdr.detectChanges();
          return;
        }
        this.saveRecording();
        if (!this.hasSavedCurrentRecording) {
          this.isStoppingRecording = false;
        }
        });
      });
    };
  }

  /** Start a new 2-minute Safari MP4 segment on the existing mic stream (after a segment rotation stop). */
  private startSafariRecorderSegment(): void {
    if (!this.audioStream) {
      return;
    }
    const format = recorderFormatForMimeType(this.activeRecordingMimeType);
    try {
      this.mediaRecorder = format.mimeType
        ? new MediaRecorder(this.audioStream, { mimeType: format.mimeType })
        : new MediaRecorder(this.audioStream);
      const effective = this.mediaRecorder.mimeType || format.mimeType;
      if (effective) {
        this.activeRecordingMimeType = effective;
      }
      this.attachMediaRecorderHandlers(this.mediaRecorder);
      this.mediaRecorder.start();
      this.scheduleNextSafariSegment();
    } catch (err) {
      console.warn('Failed to start Safari recording segment', err);
    }
  }

  private scheduleNextSafariSegment(): void {
    this.clearSafariSegmentTimer();
    if (!this.usesSafariSegmentedRecording()) {
      return;
    }
    const intervalMs = checkpointTimesliceMs(this.activeRecordingMimeType);
    this.safariSegmentTimer = setTimeout(() => {
      const recorder = this.mediaRecorder;
      if (
        !recorder ||
        recorder.state !== 'recording' ||
        !this.periodicFlushActive ||
        this.isPaused ||
        this.isStoppingRecording
      ) {
        return;
      }
      this.safariSegmentStopPending = true;
      this.stopActiveMediaRecorder();
    }, intervalMs);
  }

  private beginRecorderStop(): void {
    this.recorderStopIdle = this.recorderStopIdle.then(
      () =>
        new Promise<void>((resolve) => {
          this.resolveActiveRecorderStop = resolve;
        })
    );
  }

  private releaseRecorderStopBarrier(): void {
    this.resolveActiveRecorderStop?.();
    this.resolveActiveRecorderStop = null;
  }

  private waitForRecorderStopIdle(): Promise<void> {
    return this.recorderStopIdle;
  }

  /** Arm the deferred resolved by the {@code ondataavailable} that {@code stop()} will flush. */
  private armSegmentDataWait(): void {
    this.awaitingSegmentData = true;
    this.segmentDataPromise = new Promise<void>((resolve) => {
      this.resolveSegmentData = resolve;
    });
  }

  private resolveSegmentDataIfAwaiting(): void {
    if (!this.awaitingSegmentData) {
      return;
    }
    this.awaitingSegmentData = false;
    const resolve = this.resolveSegmentData;
    this.resolveSegmentData = null;
    resolve?.();
  }

  /**
   * After {@link MediaRecorder#stop}: wait for the paired flush {@code ondataavailable} (WebKit delivers it after
   * {@code onstop}), persist it, then upload every still-pending slice before returning so callers (pause-for-later,
   * Stop finalize) never proceed while a freshly stopped segment is unsent.
   */
  private async drainAfterRecorderStop(): Promise<void> {
    if (this.awaitingSegmentData) {
      await Promise.race([
        this.segmentDataPromise,
        new Promise<void>((r) => setTimeout(r, 5000)),
      ]);
    }
    await this.waitForPersistQueueIdle();
    // Upload the just-flushed segment (and any earlier pending slice) before returning.
    let guard = 0;
    while (
      this.periodicFlushActive &&
      this.recordingId &&
      this.firstPendingUploadChunkIndex < this.nextChunkIndex &&
      guard++ < 100
    ) {
      const before = this.firstPendingUploadChunkIndex;
      if (!this.checkpointBatchUploadInFlight) {
        this.trySchedulePeriodicCheckpointUpload();
      }
      await this.waitForCheckpointUploadIdle();
      if (this.firstPendingUploadChunkIndex === before) {
        // No progress — upload is failing/retrying; stop looping so we don't hang.
        break;
      }
    }
  }

  private stopActiveMediaRecorder(): void {
    if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
      return;
    }
    this.beginRecorderStop();
    this.armSegmentDataWait();
    try {
      this.mediaRecorder.stop();
    } catch (err) {
      console.warn('MediaRecorder.stop failed', err);
      this.resolveSegmentDataIfAwaiting();
      void this.drainAfterRecorderStop().then(() => {
        this.releaseRecorderStopBarrier();
        if (this.processingHandoff) {
          this.cleanupAudio();
        }
      });
    }
  }

  private clearSafariSegmentTimer(): void {
    if (this.safariSegmentTimer != null) {
      clearTimeout(this.safariSegmentTimer);
      this.safariSegmentTimer = null;
    }
  }

  private pauseRecording(): void {
    if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') {
      return;
    }
    this.stopRecordingKeepAlive();
    this.clearSafariSegmentTimer();
    this.isPaused = true;
    this.pausedTime = Date.now();
    this.pauseTimer();

    // Safari fMP4: pause() does not emit data — stop the current segment so audio shorter than 2 minutes is kept.
    if (this.usesSafariSegmentedRecording()) {
      this.safariSegmentStopPending = true;
      this.stopActiveMediaRecorder();
      this.pauseTailPersisted = this.waitForPausedTailPersisted();
      this.pauseTailUpload = this.pauseTailPersisted.then(() => this.flushAllCheckpointUploads());
      return;
    }
    // Pull the open timeslice out while the recorder is still running. pause() alone keeps it in the browser.
    this.armSegmentDataWait();
    try {
      this.mediaRecorder.requestData();
    } catch (err) {
      console.warn('MediaRecorder.requestData failed', err);
      this.resolveSegmentDataIfAwaiting();
    }
    this.mediaRecorder.pause();
    this.pauseTailPersisted = this.waitForPausedTailPersisted();
    this.pauseTailUpload = this.pauseTailPersisted.then(() => this.flushAllCheckpointUploads());
  }

  /** Wait until the open slice is stored locally. Does not wait for the server upload. */
  private async waitForPausedTailPersisted(): Promise<void> {
    if (this.awaitingSegmentData) {
      await Promise.race([
        this.segmentDataPromise,
        new Promise<void>((resolve) => setTimeout(resolve, 5000)),
      ]);
    }
    await this.waitForPersistQueueIdle();
  }

  private async resumeRecording(): Promise<void> {
    if (!this.isPaused) {
      return;
    }
    if (this.microphoneRetargetPending) {
      const result = await this.restartLiveCapture({ keepPaused: true });
      this.microphoneRetargetPending = result === 'skipped';
      if (!this.isPaused || this.isStoppingRecording) {
        return;
      }
    }
    this.isPaused = false;
    this.recordingStartTime += Date.now() - this.pausedTime;
    this.resumeTimer();
    this.startRecordingKeepAlive();

    const recorder = this.mediaRecorder;
    if (recorder && recorder.state === 'paused') {
      recorder.resume();
      this.scheduleNextSafariSegment();
      return;
    }
    if (this.audioStream && (!recorder || recorder.state === 'inactive')) {
      this.startRecorderOnCurrentStream();
    }
  }

  private shouldOfferShortRecordingChoice(): boolean {
    if (this.destroyingComponent) {
      return false;
    }
    if (this.silentRecorderTeardownDone) {
      return false;
    }
    // Use total visit timer (includes paused/append preloaded duration) so e.g. 8s + 22s new reaches 30s together.
    if (this.totalRecordingTime >= MIN_RECORDING_SECONDS_FOR_UPLOAD) {
      return false;
    }
    if (this.audioChunks.length === 0 && !this.anyCheckpointUploadedToServer && this.totalRecordingTime <= 0) {
      return false;
    }
    return true;
  }

  resumeAfterShortRecordingWarning(): void {
    if (!this.showShortRecordingModal || this.isRecording) {
      return;
    }
    this.showShortRecordingModal = false;
    this.continuingSameTakeAfterShortWarning = true;
    void this.onStartRecording();
  }

  startOverAfterShortRecordingWarning(): void {
    this.showShortRecordingModal = false;
    this.continuingSameTakeAfterShortWarning = false;
    this.shortRecordingAwaitingContinue = false;
    const rid = this.recordingId;
    if (rid) {
      try {
        clearRecordingDraftForId(rid);
      } catch {
        // ignore
      }
    } else {
      clearRecordingCheckpoint();
    }
    this.recordingId = null;
    this.audioChunks = [];
    this.audioChunksLogicalBase = 0;
    this.nextChunkIndex = 0;
    this.firstPendingUploadChunkIndex = 0;
    this.nextCheckpointBatchNumber = 1;
    this.anyCheckpointUploadedToServer = false;
    this.resumeTranscriptionDbId = null;
    this.pendingResumeRecordingUuid = null;
    this.appendTranscriptionDbId = null;
    this.appendCanonicalRecordingUuid = null;
    this.appendVisitHasSoap = false;
    this.preservedManifestBeforeAppend = null;
    this.manifestSnapshotBeforeResumedTake = null;
    this.hasAttemptedRecording = false;
    this.hasSavedCurrentRecording = false;
    this.isStoppingRecording = false;
    this.transcriptionText = '';
    this.analysisResult = null;
    this.visitPauseNameLocked = false;
    this.draftPatientIdForUnknown = null;
    this.selectedPatient = null;
    this.selectedPatientDisplay = '';
    this.patientInfo = {
      patientName: '',
      mrn: '',
      appointmentType: this.defaultAppointmentType,
      neurologicalSpecialty: this.defaultNeurologicalSpecialty
    };
    this.isMrnReadonly = false;
    this.resetTimer();
    this.syncCaptureActive();
    this.replaceWithFreshRecording();
    this.authService.refreshSessionIfLoggedIn().subscribe();
    this.cdr.detectChanges();
  }

  /**
   * Cancel / abandon paths. Returns true when this stop must not transcribe or offer the short-recording dialog.
   */
  private applyPendingAbandonAfterStop(): boolean {
    if (this.abandonResumedPausedTakeOnNextStop) {
      this.abandonResumedPausedTakeOnNextStop = false;
      const navTarget = this.abandonResumedPausedNavigateAfter;
      this.abandonResumedPausedNavigateAfter = null;
      const snap = this.manifestSnapshotBeforeResumedTake;
      this.manifestSnapshotBeforeResumedTake = null;
      this.isStoppingRecording = false;
      this.hasSavedCurrentRecording = false;
      this.shortRecordingAwaitingContinue = false;
      this.showShortRecordingModal = false;
      void this.finalizeAbandonResumedPausedTakeAfterRecorderStop(navTarget, snap);
      return true;
    }
    if (this.abandonAppendAdditionOnNextStop) {
      this.abandonAppendAdditionOnNextStop = false;
      const navTarget = this.abandonAppendNavigateAfter;
      this.abandonAppendNavigateAfter = null;
      const snapshotRid = this.recordingId ?? this.appendCanonicalRecordingUuid?.trim().toLowerCase() ?? null;
      const snapshotAppendDbId = this.appendTranscriptionDbId;
      this.isStoppingRecording = false;
      this.hasSavedCurrentRecording = false;
      this.shortRecordingAwaitingContinue = false;
      this.showShortRecordingModal = false;
      void this.finalizeAbandonAppendAfterRecorderStop(navTarget, snapshotRid, snapshotAppendDbId);
      return true;
    }
    if (this.discardRecordingOnNextStop) {
      this.discardRecordingOnNextStop = false;
      this.isStoppingRecording = false;
      this.hasSavedCurrentRecording = false;
      this.shortRecordingAwaitingContinue = false;
      this.showShortRecordingModal = false;
      const transcriptionId = this.pendingFullDiscardTranscriptionId;
      this.pendingFullDiscardTranscriptionId = null;
      const rid = this.recordingId;
      const nav = this.pendingDiscardNavigate;
      this.pendingDiscardNavigate = null;
      if (rid) {
        try {
          clearRecordingDraftForId(rid);
        } catch {
          // ignore
        }
      }
      this.recordingId = null;
      this.audioChunks = [];
      this.audioChunksLogicalBase = 0;
      this.nextChunkIndex = 0;
      this.firstPendingUploadChunkIndex = 0;
      this.nextCheckpointBatchNumber = 1;
      this.anyCheckpointUploadedToServer = false;
      this.appendTranscriptionDbId = null;
      this.appendCanonicalRecordingUuid = null;
      this.appendVisitHasSoap = false;
      this.resumeTranscriptionDbId = null;
      this.pendingResumeRecordingUuid = null;
      this.manifestSnapshotBeforeResumedTake = null;
      this.isRecording = false;
      this.isPaused = false;
      this.periodicFlushActive = false;
      this.stopTimer();
      this.syncCaptureActive();
      this.silentRecorderTeardownDone = true;
      this.suppressChunkPersistUntilStopHandled = false;
      this.cdr.detectChanges();
      void this.removeDiscardedVisitFromServer(transcriptionId).finally(() => {
        this.ngZone.run(() => {
          this.blockRecorderAbandonLocalChunkPersist = false;
          if (nav === 'home') {
            this.navigateHome();
          } else if (nav === 'transcriptions') {
            this.navigateToList();
          }
          this.cdr.detectChanges();
        });
      });
      return true;
    }
    return false;
  }

  private async removeDiscardedVisitFromServer(transcriptionId: number | null): Promise<void> {
    try {
      await this.pauseTailUpload.catch(() => undefined);
      await this.checkpointUploadGate.catch(() => undefined);
    } catch {
      // best-effort — delete still runs so a short visit does not stay paused
    }
    if (transcriptionId == null || !Number.isFinite(transcriptionId)) {
      return;
    }
    try {
      await firstValueFrom(
        this.transcriptionsService.deleteTranscription(String(transcriptionId)).pipe(
          catchError(() => {
            this.ngZone.run(() =>
              this.messageService.add({
                severity: 'warn',
                summary: 'Could not remove recording',
                detail: 'The visit may still be in your list. You can delete it there.'
              })
            );
            return of(undefined);
          })
        )
      );
      this.transcriptionsService.requestTranscriptionsListRefresh();
    } catch {
      // best-effort
    }
  }

  private stopRecording(): void {
    if (this.isStoppingRecording) {
      return;
    }

    this.periodicFlushActive = false;
    this.clearSafariSegmentTimer();

    this.screenWakeLockEpoch++;
    void this.releaseScreenWakeLock();
    this.isRecording = false;
    this.isPaused = false;
    this.stopTimer();
    this.syncCaptureActive();

    const recorderLive = !!this.mediaRecorder && this.mediaRecorder.state !== 'inactive';
    if (
      this.discardRecordingOnNextStop ||
      this.abandonResumedPausedTakeOnNextStop ||
      this.abandonAppendAdditionOnNextStop
    ) {
      if (recorderLive) {
        this.isStoppingRecording = true;
        this.stopActiveMediaRecorder();
      } else {
        this.cleanupAudio();
        this.ngZone.run(() => this.applyPendingAbandonAfterStop());
      }
      return;
    }

    if (this.shouldOfferShortRecordingChoice()) {
      if (recorderLive) {
        this.isStoppingRecording = true;
        this.stopActiveMediaRecorder();
      } else {
        this.isStoppingRecording = false;
        this.cleanupAudio();
        this.shortRecordingAwaitingContinue = true;
        this.showShortRecordingModal = true;
        this.cdr.detectChanges();
      }
      return;
    }

    if (!recorderLive && !this.hasPendingRecordingAudio()) {
      this.isStoppingRecording = false;
      this.cleanupAudio();
      return;
    }

    // Leave for the in-progress visit now. The last slice and checkpoint POST continue after navigation.
    this.processingHandoff = true;
    this.silentRecorderTeardownDone = true;
    this.hasSavedCurrentRecording = true;
    this.isTranscribing = true;
    if (recorderLive) {
      this.isStoppingRecording = true;
      this.stopActiveMediaRecorder();
    } else {
      this.cleanupAudio();
    }
    this.transcribeAfterStop();
  }

  /** True when Stop should flush checkpoints / transcribe (memory, IndexedDB, or prior S3 batches). */
  private hasPendingRecordingAudio(): boolean {
    return (
      this.audioChunks.length > 0 ||
      this.firstPendingUploadChunkIndex < this.nextChunkIndex ||
      this.anyCheckpointUploadedToServer
    );
  }

  private startNoAudioInputWatch(): void {
    this.clearNoAudioInputWatch();
    this.peakAudioLevelSample = 0;
    this.noAudioInputWarningShown = false;
    this.noAudioInputCheckTimer = setTimeout(() => {
      this.noAudioInputCheckTimer = null;
      if (!this.isRecording || this.noAudioInputWarningShown) {
        return;
      }
      if (this.peakAudioLevelSample < 5) {
        this.noAudioInputWarningShown = true;
        this.messageService.add({
          severity: 'warn',
          summary: 'No microphone input detected',
          detail: 'Audio levels are near zero and recording quality will not be good.',
          life: 15_000,
        });
      }
    }, 20_000);
  }

  private clearNoAudioInputWatch(): void {
    if (this.noAudioInputCheckTimer != null) {
      clearTimeout(this.noAudioInputCheckTimer);
      this.noAudioInputCheckTimer = null;
    }
    this.peakAudioLevelSample = 0;
  }

  private cleanupAudio(): void {
    this.stopRecordingKeepAlive();
    this.clearNoAudioInputWatch();
    this.clearSafariSegmentTimer();
    if (this.audioStream) {
      this.audioStream.getTracks().forEach((track) => track.stop());
      this.audioStream = null;
    }
    this.mediaRecorder = null;

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.audioAnalyser = null;
    this.audioLevel = 0;
  }

  private queuePersistRecordingChunk(chunkIndex: number, blob: Blob): void {
    if (!this.recordingId || this.blockRecorderAbandonLocalChunkPersist || this.suppressChunkPersistUntilStopHandled) {
      return;
    }
    const recordingId = this.recordingId;
    const mime = this.activeRecordingMimeType;
    const work = this.persistTail
      .then(() => {
        if (this.blockRecorderAbandonLocalChunkPersist) {
          return undefined;
        }
        return saveRecordingChunkLocal(recordingId, chunkIndex, mime, blob);
      })
      .catch((err) => {
        console.warn('Failed to persist recording chunk to local storage', err);
      });
    this.persistTail = work.then(
      () => undefined,
      () => undefined
    );
    this.chunkPersistByIndex.set(chunkIndex, work.then(() => undefined, () => undefined));
  }

  private async ensureChunksPersisted(start: number, end: number): Promise<void> {
    await this.waitForPersistQueueIdle();
    for (let i = start; i <= end; i++) {
      const p = this.chunkPersistByIndex.get(i);
      if (p) {
        await p;
      }
    }
  }

  private trySchedulePeriodicCheckpointUpload(): void {
    if (!this.periodicFlushActive || this.checkpointBatchUploadInFlight || !this.recordingId) {
      return;
    }
    const start = this.firstPendingUploadChunkIndex;
    const pending = this.nextChunkIndex - start;
    const batchSize = chunksPerCheckpointUpload(this.activeRecordingMimeType);
    if (pending < batchSize) {
      return;
    }
    const end = start + batchSize - 1;
    this.checkpointBatchUploadInFlight = true;
    void this.uploadOneCheckpointBatch(start, end)
      .then(() => {
        this.checkpointBatchUploadInFlight = false;
        this.trySchedulePeriodicCheckpointUpload();
      })
      .catch((err) => {
        console.warn('Periodic checkpoint batch upload failed', err);
        this.checkpointBatchUploadInFlight = false;
      });
  }

  private async buildCheckpointBatchBlob(recId: string, start: number, end: number, mime: string): Promise<Blob> {
    const sliceStart = start - this.audioChunksLogicalBase;
    const sliceEndExclusive = end - this.audioChunksLogicalBase + 1;
    if (sliceStart >= 0 && this.audioChunks.length >= sliceEndExclusive) {
      return new Blob(
        this.audioChunks.slice(sliceStart, sliceEndExclusive).map((c) => c.data),
        { type: mime }
      );
    }
    const parts: Blob[] = [];
    for (let i = start; i <= end; i++) {
      const fromMemory =
        i >= this.audioChunksLogicalBase
          ? this.audioChunks[i - this.audioChunksLogicalBase]?.data
          : undefined;
      if (fromMemory && fromMemory.size > 0) {
        parts.push(fromMemory);
        continue;
      }
      const fromStorage = await loadRecordingChunkLocal(recId, i);
      if (!fromStorage || fromStorage.size === 0) {
        throw new Error(
          `Checkpoint slice ${i} missing (logical ${start}-${end}, base ${this.audioChunksLogicalBase}, buffer ${this.audioChunks.length})`
        );
      }
      parts.push(fromStorage);
    }
    return new Blob(parts, { type: mime });
  }

  /**
   * Slices below {@link #audioChunksLogicalBase} are not in RAM. If IndexedDB also lost them, they were
   * already posted and deleted — skip ahead instead of failing every later chunk on the same index.
   */
  private async firstAvailableCheckpointSlice(recId: string, start: number, end: number): Promise<number> {
    for (let i = start; i <= end; i++) {
      if (i >= this.audioChunksLogicalBase) {
        return i;
      }
      const stored = await loadRecordingChunkLocal(recId, i);
      if (stored && stored.size > 0) {
        return i;
      }
    }
    return end + 1;
  }

  /** Drop uploaded slices from RAM so the next periodic stitch uses a dense buffer from {@link #audioChunksLogicalBase}. */
  private compactAudioChunksAfterCheckpointUpload(start: number, end: number): void {
    const sliceStart = start - this.audioChunksLogicalBase;
    const sliceEndExclusive = end - this.audioChunksLogicalBase + 1;
    if (sliceStart >= 0 && this.audioChunks.length >= sliceEndExclusive) {
      this.audioChunks.splice(sliceStart, sliceEndExclusive - sliceStart);
    }
    this.audioChunksLogicalBase = end + 1;
  }

  private uploadOneCheckpointBatch(rangeStart: number, rangeEnd: number, totalBatches?: number): Promise<void> {
    const run = async (): Promise<void> => {
      const recId = this.recordingId!;
      const mime = this.activeRecordingMimeType;
      const available = await this.firstAvailableCheckpointSlice(recId, rangeStart, rangeEnd);
      if (available > rangeStart) {
        this.firstPendingUploadChunkIndex = available;
      }
      if (available > rangeEnd) {
        return;
      }
      const start = available;
      const end = rangeEnd;
      await this.ensureChunksPersisted(start, end);
      const batchIndex = this.nextCheckpointBatchNumber;
      const stitched = await this.buildCheckpointBatchBlob(recId, start, end, mime);
      if (stitched.size === 0) {
        throw new Error(`Checkpoint batch ${batchIndex} is empty (chunks ${start}-${end})`);
      }
      await this.postCheckpointBatchWithRetries(stitched, recId, batchIndex, totalBatches);
      this.anyCheckpointUploadedToServer = true;
      await removeChunkKeysRangeAsync(recId, start, end);
      for (let i = start; i <= end; i++) {
        this.chunkPersistByIndex.delete(i);
      }
      this.compactAudioChunksAfterCheckpointUpload(start, end);
      this.nextCheckpointBatchNumber = batchIndex + 1;
      writeCheckpointUploadProgress(recId, end, this.nextCheckpointBatchNumber);
      this.firstPendingUploadChunkIndex = end + 1;
    };
    const chained = this.checkpointUploadGate.then(run, run);
    this.checkpointUploadGate = chained.then(
      () => undefined,
      () => undefined
    );
    return chained;
  }

  private waitForCheckpointUploadIdle(): Promise<void> {
    if (!this.checkpointBatchUploadInFlight) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const poll = (): void => {
        if (!this.checkpointBatchUploadInFlight) {
          resolve();
          return;
        }
        setTimeout(poll, 30);
      };
      poll();
    });
  }

  /** Wait until no additional local chunk persist work is appended to {@link #persistTail}. */
  private async waitForPersistQueueIdle(): Promise<void> {
    let seen = this.persistTail;
    await seen.catch(() => undefined);
    while (seen !== this.persistTail) {
      seen = this.persistTail;
      await seen.catch(() => undefined);
    }
  }

  /**
   * @returns true when every slice through {@link #nextChunkIndex} has been uploaded to S3; false if any batch failed
   *   or the session id was cleared while work remained (caller must not finalize partial checkpoints).
   */
  private flushAllCheckpointUploads(): Promise<boolean> {
    return this.persistTail
      .then(() => this.waitForCheckpointUploadIdle())
      .then(() => this.flushCheckpointBatchesUntilDone())
      .catch((e) => {
        console.warn('Checkpoint flush incomplete', e);
        return false;
      });
  }

  private async flushCheckpointBatchesUntilDone(): Promise<boolean> {
    const batchSize = chunksPerCheckpointUpload(this.activeRecordingMimeType);
    const initialPendingSpan = this.nextChunkIndex - this.firstPendingUploadChunkIndex;
    const uploadsInThisFlush =
      initialPendingSpan > 0 ? Math.ceil(initialPendingSpan / batchSize) : 0;
    const totalBatchesForFlush =
      uploadsInThisFlush > 0 ? this.nextCheckpointBatchNumber - 1 + uploadsInThisFlush : undefined;
    while (this.firstPendingUploadChunkIndex < this.nextChunkIndex && this.recordingId) {
      if (this.checkpointBatchUploadInFlight) {
        await this.waitForCheckpointUploadIdle();
      }
      const start = this.firstPendingUploadChunkIndex;
      const end = Math.min(start + batchSize - 1, this.nextChunkIndex - 1);
      try {
        this.checkpointBatchUploadInFlight = true;
        await this.uploadOneCheckpointBatch(start, end, totalBatchesForFlush);
        this.checkpointBatchUploadInFlight = false;
      } catch (e) {
        console.warn('Checkpoint batch upload failed while flushing', e);
        this.checkpointBatchUploadInFlight = false;
        return false;
      }
    }
    return this.firstPendingUploadChunkIndex >= this.nextChunkIndex;
  }

  private saveRecording(): void {
    if (this.hasSavedCurrentRecording) {
      return;
    }
    if (!this.hasPendingRecordingAudio()) {
      return;
    }
    this.hasSavedCurrentRecording = true;
    this.isStoppingRecording = false;
    this.transcribeAfterStop();
  }

  /** One merged WebM for all slices — only used when S3 finalize is unavailable (no checkpoints or finalize failed). */
  private buildFullRecordingWebmBlob(): Blob {
    const mime = this.activeRecordingMimeType || 'audio/webm;codecs=opus';
    return new Blob(this.audioChunks.map((chunk) => chunk.data), { type: mime });
  }

  private clearRecordingBufferAfterStop(): void {
    this.audioChunks = [];
    this.audioChunksLogicalBase = 0;
    this.nextCheckpointBatchNumber = 1;
    this.totalRecordingTime = 0;
  }

  /**
   * Restores the next S3 batch index from manifest; legacy manifests without the field use a best-effort guess
   * from chunk count (may be wrong if an older client overwrote S3 keys — prefer a fresh recording in that case).
   */
  private inferNextCheckpointBatchNumberFromManifest(m: {
    lastChunkIndex: number;
    nextCheckpointBatchNumber?: number;
    recorderMimeType?: string;
  }): number {
    if (typeof m.nextCheckpointBatchNumber === 'number' && m.nextCheckpointBatchNumber >= 1) {
      return m.nextCheckpointBatchNumber;
    }
    const perBatch = chunksPerCheckpointUpload(m.recorderMimeType);
    // No {@link nextCheckpointBatchNumber} in manifest — assume no batches on S3 yet; start at 1.
    return Math.max(1, Math.ceil((m.lastChunkIndex + 1) / perBatch));
  }

  /**
   * True when the draft manifest shows at least one stitched checkpoint batch was successfully POSTed to S3
   * ({@link writeCheckpointNextBatchNumber} advances {@link RecordingCheckpointState#nextCheckpointBatchNumber} past 1).
   * {@link RecordingCheckpointState#lastChunkIndex} only reflects local slice persistence — it must not be used alone here.
   */
  private manifestIndicatesServerCheckpointBatches(
    m: Pick<RecordingCheckpointState, 'lastChunkIndex' | 'nextCheckpointBatchNumber' | 'recorderMimeType'>
  ): boolean {
    const n = m.nextCheckpointBatchNumber;
    if (typeof n === 'number' && n > 1) {
      return true;
    }
    if (typeof n === 'number') {
      return false;
    }
    const perBatch = chunksPerCheckpointUpload(m.recorderMimeType);
    return m.lastChunkIndex >= perBatch - 1;
  }

  private async postCheckpointBatchWithRetries(
    stitched: Blob,
    recId: string,
    batchIndex: number,
    totalBatches?: number
  ): Promise<void> {
    let lastErr: unknown;
    const fileExtension = fileExtensionForMimeType(this.activeRecordingMimeType);
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await firstValueFrom(
          this.recordingService.uploadAudioCheckpointBatch(stitched, recId, batchIndex, totalBatches, fileExtension)
        );
        return;
      } catch (e) {
        lastErr = e;
        if (attempt < 3) {
          await new Promise((r) => setTimeout(r, 400 * attempt));
        }
      }
    }
    throw lastErr;
  }

  /**
   * Open Transcriptions as soon as post-stop processing begins. iOS WKWebView often omits
   * HttpClient upload progress events, so we must not wait for onUploadStarted.
   */
  private ensureProcessingNavigation(
    summary = 'Processing started',
    detail?: string
  ): void {
    if (this.processingNavigateTriggered) {
      return;
    }
    this.processingNavigateTriggered = true;
    const navDetail =
      detail ??
      (this.inNewShell
        ? 'Opening Visits. Processing continues in the background.'
        : 'Opening Transcriptions. Processing continues in the background.');
    this.ngZone.run(() => {
      const patientName =
        this.patientInfo.patientName?.trim() ||
        this.selectedPatientDisplay?.trim() ||
        this.pauseModalPatientName?.trim() ||
        '';
      // Resume / append updates an existing visit row — do not seed a second optimistic card.
      const existingVisitId = this.resumeTranscriptionDbId ?? this.appendTranscriptionDbId;
      if (existingVisitId != null && Number.isFinite(existingVisitId)) {
        this.transcriptionsService.clearPendingRecordingVisit();
        this.transcriptionsService.setRecordingUploadAwaitingResult(true);
        this.transcriptionsService.markNotesBuilding(existingVisitId, { rebuild: true });
        this.transcriptionsService.setRecordingUploadSpinnerLinkedRow(existingVisitId);
      } else {
        this.transcriptionsService.seedPendingRecordingVisit(patientName);
        this.transcriptionsService.setRecordingUploadAwaitingResult(true);
      }
      this.messageService.add({
        severity: 'info',
        summary,
        detail: navDetail
      });
      this.navigateToList();
    });
  }

  /**
   * Prefer server-side assembly from S3 checkpoints; avoid merging all slices in the browser unless fallback upload is needed.
   */
  private transcribeAfterStop(): void {
    this.isTranscribing = true;
    this.ensureProcessingNavigation();
    const onConvertError = () => {
      this.isTranscribing = false;
      this.transcriptionsService.clearRecordingUploadAwaiting(
        'failure',
        'Failed to convert audio format for transcription.'
      );
      this.messageService.add({
        severity: 'error',
        summary: 'Audio Conversion Failed',
        detail: 'Failed to convert audio format for transcription.'
      });
      this.resetTimer();
    };
    const afterPatientReady = async () => {
      await this.waitForRecorderStopIdle();
      await this.drainAfterRecorderStop();
      const flushComplete = await this.flushAllCheckpointUploads();
      const rid = this.recordingId;
      const allChunksUploaded = this.firstPendingUploadChunkIndex >= this.nextChunkIndex;
      const serverFinalizeReady =
        !!rid && this.anyCheckpointUploadedToServer && flushComplete && allChunksUploaded;
      if (!serverFinalizeReady && this.anyCheckpointUploadedToServer) {
        console.warn('Checkpoint finalize prerequisites not met', {
          flushComplete,
          allChunksUploaded,
          nextChunkIndex: this.nextChunkIndex,
          firstPendingUploadChunkIndex: this.firstPendingUploadChunkIndex,
          nextCheckpointBatchNumber: this.nextCheckpointBatchNumber,
          recordingId: rid
        });
      }
      if (serverFinalizeReady) {
        this.tryFinalizeCheckpointThenBlob(rid!, () => this.buildFullRecordingWebmBlob(), onConvertError);
      } else if (this.anyCheckpointUploadedToServer) {
        this.failTranscriptionUpload(
          'Upload Incomplete',
          'Tap Stop again to retry.'
        );
      } else {
        const durationForApi = this.formatDurationForApi(this.totalRecordingTime);
        this.uploadLocalRecordingBlob(this.buildFullRecordingWebmBlob(), durationForApi, onConvertError);
      }
    };
    this.ensurePatientMrnBeforeTranscribe(() => void afterPatientReady(), (msg) => {
      this.isTranscribing = false;
      this.transcriptionsService.clearRecordingUploadAwaiting('failure', msg);
      this.messageService.add({
        severity: 'error',
        summary: 'Patient Required',
        detail: msg
      });
      this.resetTimer();
    });
  }

  /**
   * Reuse paused-draft / unknown patient MRN on stop; only create a new patient for typed names without an MRN.
   */
  private ensurePatientMrnBeforeTranscribe(onReady: () => void, onFailed: (message: string) => void): void {
    if (this.selectedPatient || this.patientInfo.mrn?.trim()) {
      onReady();
      return;
    }
    if (this.draftPatientIdForUnknown != null) {
      this.patientsService.getPatientById(this.draftPatientIdForUnknown).subscribe({
        next: (patient: PatientResponse) => {
          this.patientInfo.mrn = patient.mrn ?? '';
          onReady();
        },
        error: (err) => {
          onFailed(err?.error?.message ?? err?.message ?? 'Failed to load patient.');
        }
      });
      return;
    }
    if (this.resumeTranscriptionDbId != null) {
      this.transcriptionsService.getTranscriptionById(String(this.resumeTranscriptionDbId)).subscribe({
        next: (detail) => {
          const mrn = (detail.mrn ?? '').trim();
          if (mrn) {
            this.patientInfo.mrn = mrn;
            if (detail.patientId != null) {
              this.draftPatientIdForUnknown = detail.patientId;
            }
            onReady();
            return;
          }
          if (detail.patientId != null) {
            this.draftPatientIdForUnknown = detail.patientId;
            this.patientsService.getPatientById(detail.patientId).subscribe({
              next: (patient: PatientResponse) => {
                this.patientInfo.mrn = patient.mrn ?? '';
                onReady();
              },
              error: (err) => {
                onFailed(err?.error?.message ?? err?.message ?? 'Failed to load patient.');
              }
            });
            return;
          }
          onReady();
        },
        error: () => {
          onFailed('Could not load paused visit patient. Try again from the transcriptions list.');
        }
      });
      return;
    }
    const name = this.patientInfo.patientName?.trim();
    const needsUnknownPatient = !name || this.isUnknownDisplayName(name);
    const displayName = needsUnknownPatient ? 'Unknown Patient' : name;
    const parts = displayName.split(/\s+/).filter(Boolean);
    const firstName = parts[0] ?? 'Unknown';
    const lastName = parts.slice(1).join(' ') || firstName;
    this.patientsService.createPatient({ firstName, lastName }).subscribe({
      next: (patient: PatientResponse) => {
        this.patientInfo.mrn = patient.mrn ?? '';
        this.patientInfo.patientName =
          `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim() || displayName;
        if (needsUnknownPatient && patient.id != null) {
          this.draftPatientIdForUnknown = patient.id;
        }
        onReady();
      },
      error: (err) => {
        onFailed(err?.error?.message ?? err?.message ?? 'Failed to create patient.');
      }
    });
  }

  /**
   * Server assembles S3 checkpoints into one MP3. Fallback builds one client-side WebM only if finalize fails.
   */
  private tryFinalizeCheckpointThenBlob(
    recordingId: string,
    buildFallbackWebm: () => Blob,
    onConvertError: () => void
  ): void {
    let didEarlyNavigate = false;
    const triggerNavigate = () => {
      didEarlyNavigate = true;
      this.ensureProcessingNavigation();
    };
    triggerNavigate();
    const totalBatches = Math.max(1, this.nextCheckpointBatchNumber - 1);
    const durationForApi = this.formatDurationForApi(this.totalRecordingTime);
    this.recordingService
      .finalizeCheckpointRecording(
        recordingId,
        totalBatches,
        this.patientInfo,
        durationForApi,
        this.resumeTranscriptionDbId ?? undefined,
        this.appendTranscriptionDbId ?? undefined
      )
      .subscribe({
        next: (response) => {
          this.ngZone.run(() => {
            if (response.status === 'CLIENT_FALLBACK') {
              this.applyCheckpointFinalizeFallbackToFullBlob(
                didEarlyNavigate,
                buildFallbackWebm,
                onConvertError
              );
              return;
            }
            this.clearRecordingBufferAfterStop();
            // localStorage: per-slice keys are removed after each successful checkpoint POST (uploadOneCheckpointBatch).
            // Manifest + remaining chunk keys are cleared here only after finalize HTTP 200 (server has written final MP3; S3 draft deletes may still complete async).
            clearRecordingDraftForId(recordingId);
            this.recordingId = null;
            this.resumeTranscriptionDbId = null;
            this.pendingResumeRecordingUuid = null;
            this.appendTranscriptionDbId = null;
            this.appendCanonicalRecordingUuid = null;
            this.appendVisitHasSoap = false;
            this.restorePreservedManifestAfterAppendIfNeeded();
            this.isTranscribing = false;
            this.resetTimer();
            this.transcriptionsService.setRecordingUploadAwaitingResult(false);
            this.transcriptionsService.markNotesBuilding(response?.id);
            this.transcriptionsService.setRecordingUploadSpinnerLinkedRow(response?.id);
            // Visit row exists (or was updated) — stop the upload-awaiting timer immediately so
            // long SOAP generation does not surface a false "timed out" banner.
            this.transcriptionsService.requestTranscriptionsListRefresh();
            this.showTranscriptionUploadResultToast(response);
          });
        },
        error: () => {
          this.ngZone.run(() => {
            this.applyCheckpointFinalizeFallbackToFullBlob(
              didEarlyNavigate,
              buildFallbackWebm,
              onConvertError
            );
          });
        }
      });
  }

  /** Server asked for full-recording upload when local slices are still available (no S3 checkpoints). */
  private applyCheckpointFinalizeFallbackToFullBlob(
    didEarlyNavigate: boolean,
    buildFallbackWebm: () => Blob,
    onConvertError: () => void
  ): void {
    if (didEarlyNavigate) {
      this.transcriptionsService.clearRecordingUploadAwaiting('dismiss');
    }
    if (this.anyCheckpointUploadedToServer) {
      this.failTranscriptionUpload(
        'Processing Failed',
        'Tap Stop again to retry.'
      );
      return;
    }
    const durationForApi = this.formatDurationForApi(this.totalRecordingTime);
    this.uploadLocalRecordingBlob(buildFallbackWebm(), durationForApi, onConvertError);
  }

  private failTranscriptionUpload(summary: string, detail: string): void {
    this.isTranscribing = false;
    this.processingNavigateTriggered = false;
    this.hasSavedCurrentRecording = false;
    this.transcriptionsService.clearRecordingUploadAwaiting('failure', detail);
    this.messageService.add({
      severity: 'error',
      summary,
      detail
    });
    this.resetTimer();
  }

  private uploadLocalRecordingBlob(webm: Blob, durationForApi: string, onConvertError: () => void): void {
    if (webm.size === 0) {
      this.failTranscriptionUpload(
        'No Audio',
        'No recording data was found to upload. Record again or resume the visit on the same device.'
      );
      return;
    }
    this.clearRecordingBufferAfterStop();
    this.convertToMp3(webm)
      .then((mp3) => this.subscribeTranscriptionUpload(mp3, durationForApi))
      .catch(onConvertError);
  }

  private subscribeTranscriptionUpload(mp3Blob: Blob, durationForApi?: string): void {
    if (mp3Blob.size === 0) {
      this.failTranscriptionUpload(
        'No Audio',
        'No recording data was found to upload. Record again or resume the visit on the same device.'
      );
      return;
    }
    let didEarlyNavigate = this.processingNavigateTriggered;
    this.ensureProcessingNavigation('Upload started');
    didEarlyNavigate = true;
    this.recordingService
      .transcribeAudio(mp3Blob, this.patientInfo, durationForApi ?? this.recordingTime, {
        appendTranscriptionId:
          this.appendTranscriptionDbId != null && Number.isFinite(this.appendTranscriptionDbId)
            ? this.appendTranscriptionDbId
            : undefined,
        resumeTranscriptionId:
          this.appendTranscriptionDbId == null &&
          this.resumeTranscriptionDbId != null &&
          Number.isFinite(this.resumeTranscriptionDbId)
            ? this.resumeTranscriptionDbId
            : undefined
      })
      .subscribe({
        next: (response) => {
          this.ngZone.run(() => {
            const rid = this.recordingId;
            if (rid) {
              clearRecordingDraftForId(rid);
            } else {
              clearRecordingCheckpoint();
            }
            this.recordingId = null;
            this.resumeTranscriptionDbId = null;
            this.pendingResumeRecordingUuid = null;
            this.appendTranscriptionDbId = null;
            this.appendCanonicalRecordingUuid = null;
            this.appendVisitHasSoap = false;
            this.restorePreservedManifestAfterAppendIfNeeded();
            this.isTranscribing = false;
            this.resetTimer();
            if (didEarlyNavigate) {
              this.transcriptionsService.setRecordingUploadAwaitingResult(false);
              this.transcriptionsService.markNotesBuilding(response?.id);
              this.transcriptionsService.setRecordingUploadSpinnerLinkedRow(response?.id);
              this.transcriptionsService.requestTranscriptionsListRefresh();
              this.showTranscriptionUploadResultToast(response);
            } else {
              this.messageService.add({
                severity: 'success',
                summary: 'Recording Processed',
                detail: this.inNewShell
                  ? 'Your recording is being processed. You will be redirected to Visits.'
                  : 'Your recording is being processed. You will be redirected to the transcriptions list.'
              });
              setTimeout(() => this.navigateToList(), 1000);
            }
          });
        },
        error: (error) => {
          this.ngZone.run(() => {
            this.isTranscribing = false;
            const errorMessage =
              error?.error?.error || error?.message || 'Failed to upload recording. Please try again.';
            if (didEarlyNavigate) {
              this.transcriptionsService.clearRecordingUploadAwaiting('failure', errorMessage);
            }
            this.messageService.add({
              severity: 'error',
              summary: 'Transcription Failed',
              detail: errorMessage
            });
            this.resetTimer();
          });
        }
      });
  }

  private async convertToMp3(webmBlob: Blob): Promise<Blob> {
    // For now, we'll use the WebM blob directly since the API might accept it
    // In a production environment, you'd want to use a proper audio conversion library
    // like ffmpeg.js or similar to convert WebM to MP3
    
    // Create a copy with MP3 extension for the API
    return new Blob([webmBlob], { type: 'audio/mpeg' });
  }

  startTimer(): void {
    this.recordingTime = this.formatClockFromSeconds(this.totalRecordingTime);
    this.updateTimerDisplay();
    this.recordingInterval = setInterval(() => {
      this.totalRecordingTime++;
      this.recordingTime = this.formatClockFromSeconds(this.totalRecordingTime);
      this.updateTimerDisplay();
    }, 1000);
  }

  private updateTimerDisplay(): void {
    // Direct DOM update - bypass Angular completely
    const timerElement = document.getElementById('recording-timer');
    if (timerElement) {
      timerElement.textContent = this.recordingTime;
    } else {
      // Try alternative selector
      const altElement = document.querySelector('.timer span');
      if (altElement) {
        altElement.textContent = this.recordingTime;
      }
    }
  }

  private pauseTimer(): void {
    // Stop the timer interval but keep the current time
    if (this.recordingInterval) {
      clearInterval(this.recordingInterval);
      this.recordingInterval = null;
    }
  }

  private resumeTimer(): void {
    if (!this.recordingInterval) {
      this.recordingInterval = setInterval(() => {
        this.totalRecordingTime++;
        this.recordingTime = this.formatClockFromSeconds(this.totalRecordingTime);
        this.updateTimerDisplay();
      }, 1000);
    }
  }

  stopTimer(): void {
    if (this.recordingInterval) {
      clearInterval(this.recordingInterval);
      this.recordingInterval = null;
    }
  }

  private resetTimer(): void {
    this.stopTimer();
    this.shortRecordingAwaitingContinue = false;
    this.recordingTime = '00:00';
    this.totalRecordingTime = 0;
    this.updateTimerDisplay();
  }

  areRequiredFieldsFilled(): boolean {
    return true;
  }

  isPatientInfoValid(): boolean {
    if (!this.hasAttemptedRecording) return false;
    if (!this.patientInfo.patientName?.trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Patient name is required!'
      });
      return false;
    }
    return true;
  }

  onCancel(): void {
    if (this.orphanResumeBlocksRecording) {
      return;
    }
    if (!this.isRecording && !this.isPaused) {
      this.navigateHome();
      return;
    }
    // Adding to a completed visit: drop only this addition. The finished note stays.
    if (!this.canPauseForLaterFromCancel) {
      this.beginAbandonAppendAddition('transcriptions');
      return;
    }
    // Under 30s the visit is not worth keeping — drop the draft, including a short paused row.
    if (this.totalRecordingTime < MIN_RECORDING_SECONDS_FOR_UPLOAD) {
      this.beginDiscardEntireRecording('transcriptions');
      return;
    }
    // Resumed paused visit already past 30s: keep the earlier audio, drop only this take.
    if (this.shouldAbandonResumedPausedMicTake()) {
      this.beginAbandonResumedPausedTake('transcriptions');
      return;
    }
    // New visit or resumed paused visit: pause-for-later directly (no “leave?” modal).
    this.postPauseNavigateTarget = 'transcriptions';
    this.pauseModalOpenedFrom = 'cancel';
    if (!this.isPaused) {
      this.pauseRecording();
    }
    if (!this.shouldShowPausePatientDialog()) {
      void this.finishPauseForLater(true);
    } else {
      this.pauseModalPatientName = this.getPauseModalInitialName();
      this.pauseModalPatientSuggestions = this.allPatients.slice(0, 10);
      this.showPauseForLaterDialog = true;
    }
    this.cdr.detectChanges();
  }

  /** Cancel on a visit under 30 seconds: stop the mic, drop local audio, and delete the paused row if one exists. */
  private beginDiscardEntireRecording(navigateAfter: 'transcriptions' | 'home'): void {
    this.showPauseForLaterDialog = false;
    this.pauseModalOpenedFrom = null;
    this.pendingFullDiscardTranscriptionId = this.resumeTranscriptionDbId;
    this.abandonResumedPausedTakeOnNextStop = false;
    this.abandonResumedPausedNavigateAfter = null;
    this.abandonAppendAdditionOnNextStop = false;
    this.abandonAppendNavigateAfter = null;
    this.blockRecorderAbandonLocalChunkPersist = true;
    this.suppressChunkPersistUntilStopHandled = true;
    this.discardRecordingOnNextStop = true;
    this.pendingDiscardNavigate = navigateAfter;
    this.periodicFlushActive = false;
    this.stopRecording();
    this.cdr.detectChanges();
  }

  /** List-resume mic session: user chose Cancel — drop new slices and restore pre-mic manifest, then go to list. */
  private shouldAbandonResumedPausedMicTake(): boolean {
    return (
      this.isPausedVisitResumeFlow &&
      this.manifestSnapshotBeforeResumedTake != null &&
      (this.isRecording || this.isPaused)
    );
  }

  private beginAbandonResumedPausedTake(navigateAfter: 'transcriptions' | 'home' | null): void {
    if (!this.manifestSnapshotBeforeResumedTake) {
      this.navigateToList();
      return;
    }
    this.abandonAppendAdditionOnNextStop = false;
    this.abandonAppendNavigateAfter = null;
    this.blockRecorderAbandonLocalChunkPersist = true;
    this.abandonResumedPausedTakeOnNextStop = true;
    this.suppressChunkPersistUntilStopHandled = true;
    this.abandonResumedPausedNavigateAfter = navigateAfter;
    this.periodicFlushActive = false;
    this.stopRecording();
    this.cdr.detectChanges();
  }

  /** Remove S3 batches uploaded after this paused visit was resumed. Earlier batches stay with the paused row. */
  private async discardResumedCheckpointBatchesOnServer(
    snapshot: RecordingCheckpointState | null
  ): Promise<void> {
    if (!snapshot) {
      return;
    }
    const rid = snapshot.recordingId?.trim();
    const pausedId = snapshot.pausedTranscriptionDbId ?? this.resumeTranscriptionDbId;
    if (!rid || pausedId == null || !Number.isFinite(pausedId)) {
      return;
    }
    const fromBatch = this.inferNextCheckpointBatchNumberFromManifest(snapshot);
    await firstValueFrom(
      this.recordingService.discardResumedCheckpointBatches(rid, pausedId, fromBatch).pipe(
        catchError(() => {
          this.ngZone.run(() =>
            this.messageService.add({
              severity: 'warn',
              summary: 'Could not remove new audio from server',
              detail: 'The earlier paused visit is still saved. Try again if the new audio is still attached.'
            })
          );
          return of(undefined);
        })
      )
    );
  }

  private async finalizeAbandonResumedPausedTakeAfterRecorderStop(
    navTarget: 'transcriptions' | 'home' | null,
    snapshot: RecordingCheckpointState | null
  ): Promise<void> {
    try {
      await this.waitForPersistQueueIdle();
      await this.waitForCheckpointUploadIdle();
      await this.pauseTailUpload.catch(() => undefined);
      await this.checkpointUploadGate;
      await this.discardResumedCheckpointBatchesOnServer(snapshot);
    } catch {
      // best-effort
    }
    this.ngZone.run(() => {
      try {
        this.recordingId = null;
        if (snapshot?.recordingId) {
          const rid = snapshot.recordingId;
          const floor = snapshot.lastChunkIndex + 1;
          removeChunkKeysFromIndex(rid, floor);
          restoreRecordingCheckpointManifest(snapshot);
        }
        this.audioChunks = [];
        this.audioChunksLogicalBase = 0;
        this.nextChunkIndex = 0;
        this.firstPendingUploadChunkIndex = 0;
        this.nextCheckpointBatchNumber = 1;
        this.anyCheckpointUploadedToServer = false;
        this.appendTranscriptionDbId = null;
        this.appendCanonicalRecordingUuid = null;
        this.appendVisitHasSoap = false;
        this.preservedManifestBeforeAppend = null;
        this.resumeTranscriptionDbId = null;
        this.pendingResumeRecordingUuid = null;
        this.manifestSnapshotBeforeResumedTake = null;
        this.isRecording = false;
        this.isPaused = false;
        this.periodicFlushActive = false;
        this.stopTimer();
        this.syncCaptureActive();
        this.silentRecorderTeardownDone = true;
        if (navTarget === 'home') {
          this.navigateHome();
        } else {
          this.navigateToList();
        }
        this.cdr.detectChanges();
      } finally {
        this.suppressChunkPersistUntilStopHandled = false;
      }
    });
    await this.waitForPersistQueueIdle();
    this.blockRecorderAbandonLocalChunkPersist = false;
  }

  /** Discard append session: stop recorder; onstop clears local draft and optionally deletes S3 checkpoint batches only. */
  private beginAbandonAppendAddition(navigateAfter: 'transcriptions' | 'home' | 'freshRecording' | null): void {
    if (this.orphanResumeBlocksRecording || !this.isAppendVisitFlow) {
      this.resolveAppendAbandonRouteLeave?.();
      this.resolveAppendAbandonRouteLeave = null;
      return;
    }
    this.abandonResumedPausedTakeOnNextStop = false;
    this.abandonResumedPausedNavigateAfter = null;
    this.blockRecorderAbandonLocalChunkPersist = true;
    this.abandonAppendAdditionOnNextStop = true;
    this.suppressChunkPersistUntilStopHandled = true;
    this.abandonAppendNavigateAfter = navigateAfter;
    this.discardRecordingOnNextStop = false;
    this.periodicFlushActive = false;
    this.stopRecording();
    this.cdr.detectChanges();
  }

  private async finalizeAbandonAppendAfterRecorderStop(
    navTarget: 'transcriptions' | 'home' | 'freshRecording' | null,
    rid: string | null,
    appendDbId: number | null
  ): Promise<void> {
    let shouldDeleteOnServer = false;
    try {
      await this.waitForPersistQueueIdle();
      await this.waitForCheckpointUploadIdle();
      await this.checkpointUploadGate;
      shouldDeleteOnServer =
        rid != null &&
        appendDbId != null &&
        Number.isFinite(appendDbId);
      if (shouldDeleteOnServer) {
        await firstValueFrom(
          this.recordingService.abandonAppendCheckpointDraft(rid, appendDbId).pipe(
            catchError(() => {
              this.ngZone.run(() =>
                this.messageService.add({
                  severity: 'warn',
                  summary: 'Could not remove draft audio from server',
                  detail: 'Your visit recording is unchanged. Try again later if this persists.'
                })
              );
              return of(undefined);
            })
          )
        );
      }
    } catch {
      // best-effort abandon
    }
    const completeRouteLeave = this.resolveAppendAbandonRouteLeave;
    this.resolveAppendAbandonRouteLeave = null;
    this.ngZone.run(() => {
      try {
        // Drop active id before clearing storage so late recorder / persist callbacks cannot re-queue writes.
        this.recordingId = null;
        if (rid) {
          try {
            clearRecordingDraftForId(rid);
          } catch {
            // ignore
          }
        }
        this.audioChunks = [];
        this.audioChunksLogicalBase = 0;
        this.nextChunkIndex = 0;
        this.firstPendingUploadChunkIndex = 0;
        this.nextCheckpointBatchNumber = 1;
        this.anyCheckpointUploadedToServer = false;
        this.appendTranscriptionDbId = null;
        this.appendCanonicalRecordingUuid = null;
        this.appendVisitHasSoap = false;
        this.restorePreservedManifestAfterAppendIfNeeded();
        this.isRecording = false;
        this.isPaused = false;
        this.periodicFlushActive = false;
        this.stopTimer();
        this.syncCaptureActive();
        this.silentRecorderTeardownDone = true;
        if (navTarget === 'freshRecording') {
          this.resetRecordingWorkspaceForNewVisit({ afterDiscardedAppend: true });
        } else if (navTarget === 'home') {
          this.navigateHome();
        } else if (navTarget === 'transcriptions') {
          this.navigateToList();
        }
        completeRouteLeave?.();
        this.cdr.detectChanges();
      } finally {
        this.suppressChunkPersistUntilStopHandled = false;
      }
    });
    await this.waitForPersistQueueIdle();
    this.blockRecorderAbandonLocalChunkPersist = false;
  }

  private restorePreservedManifestAfterAppendIfNeeded(): void {
    if (!this.preservedManifestBeforeAppend) {
      return;
    }
    const saved = this.preservedManifestBeforeAppend;
    const current = readRecordingCheckpoint();
    if (!current || current.recordingId !== saved.recordingId) {
      restoreRecordingCheckpointManifest(saved);
    }
    this.preservedManifestBeforeAppend = null;
  }

  /** Router guard: leaving the recording page while capture is active. */
  confirmLeaveRecordingRoute(): boolean | Observable<boolean> {
    if (this.silentRecorderTeardownDone || (!this.isRecording && !this.isPaused)) {
      return true;
    }
    if (this.isTranscribing) {
      return window.confirm('A transcription is still running. Leave this page anyway?');
    }
    if (this.isAppendVisitFlow) {
      if (!window.confirm('Leave this page? Your current addition will be discarded.')) {
        return false;
      }
      return new Observable<boolean>((sub) => {
        this.resolveAppendAbandonRouteLeave = () => {
          sub.next(true);
          sub.complete();
          this.resolveAppendAbandonRouteLeave = null;
        };
        this.beginAbandonAppendAddition(null);
      });
    }
    this.postPauseNavigateTarget = null;
    this.pauseModalOpenedFrom = 'navigate';
    this.showNavigatePauseWarning = true;
    this.routeLeaveResolver = new Subject<boolean>();
    return this.routeLeaveResolver.asObservable();
  }

  onNavigatePauseWarningCancel(): void {
    this.showNavigatePauseWarning = false;
    this.postPauseNavigateTarget = null;
    this.pauseModalOpenedFrom = null;
    this.routeLeaveResolver?.next(false);
    this.routeLeaveResolver?.complete();
    this.routeLeaveResolver = null;
  }

  onNavigatePauseWarningContinue(): void {
    this.showNavigatePauseWarning = false;
    if (this.isAppendVisitFlow) {
      const after =
        this.postPauseNavigateTarget === 'freshRecording' ? 'freshRecording' : 'transcriptions';
      this.postPauseNavigateTarget = null;
      this.pauseModalOpenedFrom = null;
      this.beginAbandonAppendAddition(after);
      return;
    }
    if (!this.isPaused) {
      this.pauseRecording();
    }
    if (!this.shouldShowPausePatientDialog()) {
      void this.finishPauseForLater(true);
      return;
    }
    this.pauseModalPatientName = this.getPauseModalInitialName();
    this.pauseModalPatientSuggestions = this.allPatients.slice(0, 10);
    this.showPauseForLaterDialog = true;
    this.cdr.detectChanges();
  }

  onDismissPauseForLaterDialog(): void {
    this.showPauseForLaterDialog = false;
    this.pauseModalPatientSuggestions = [];
    const from = this.pauseModalOpenedFrom;
    this.pauseModalOpenedFrom = null;
    if (from === 'navigate' || from === 'cancel') {
      this.routeLeaveResolver?.next(false);
      this.routeLeaveResolver?.complete();
      this.routeLeaveResolver = null;
    }
  }

  onPauseForLaterSkip(): void {
    void this.finishPauseForLater(false);
  }

  onPauseForLaterConfirm(): void {
    void this.finishPauseForLater(true);
  }

  canConfirmPausePatientName(): boolean {
    if (this.selectedPatient) {
      return true;
    }
    return !!this.pauseModalPatientName?.trim();
  }

  private shouldShowPausePatientDialog(): boolean {
    return !this.visitPauseNameLocked && !this.selectedPatient;
  }

  /** Phone / narrow shell: list and detail are separate screens. */
  private isCompactShell(): boolean {
    return this.inNewShell && prefersCompactShell();
  }

  private parseDurationToSeconds(duration: string | undefined): number {
    if (!duration?.trim()) {
      return 0;
    }
    const parts = duration.trim().split(':').map((p) => parseInt(p.trim(), 10));
    if (parts.some((n) => Number.isNaN(n))) {
      return 0;
    }
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
    if (parts.length === 1) {
      return parts[0];
    }
    return 0;
  }

  private formatClockFromSeconds(totalSeconds: number): string {
    const t = Math.max(0, Math.floor(totalSeconds));
    const m = Math.floor(t / 60);
    const s = t % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  private formatDurationForApi(totalSeconds: number): string {
    const t = Math.max(0, Math.floor(totalSeconds));
    const h = Math.floor(t / 3600);
    const m = Math.floor((t % 3600) / 60);
    const s = t % 60;
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  private isUnknownDisplayName(name: string | undefined): boolean {
    const n = (name ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
    return n === '' || n === 'unknown' || n === 'unknown patient' || n === 'unknown unknown';
  }

  private getPauseModalInitialName(): string {
    const current =
      this.selectedPatientDisplay?.trim() ||
      this.patientInfo.patientName?.trim() ||
      '';
    return this.isUnknownDisplayName(current) ? '' : current;
  }

  private async finishPauseForLater(useModalName: boolean): Promise<void> {
    if (!this.recordingId || this.savingPausedVisit) {
      return;
    }
    if (this.appendTranscriptionDbId != null && this.resumeTranscriptionDbId == null) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot pause for later',
        detail:
          'Use Stop to save your addition to this visit. Pausing for later is only available when starting a new visit.'
      });
      this.showPauseForLaterDialog = false;
      this.showNavigatePauseWarning = false;
      this.pauseModalOpenedFrom = null;
      this.postPauseNavigateTarget = null;
      this.routeLeaveResolver?.next(false);
      this.routeLeaveResolver?.complete();
      this.routeLeaveResolver = null;
      return;
    }
    this.savingPausedVisit = true;
    this.cdr.detectChanges();
    try {
      await this.pauseTailPersisted;
    } catch {
      // The tail stays in the recorder buffer only if persist failed; still save the visit row.
    }
    void this.pauseTailUpload.then((flushComplete) => {
      if (!flushComplete) {
        console.warn('Pause tail upload did not finish');
      }
    });
    const rid = this.recordingId;
    const body: PauseRecordingDraftRequest = {
      recordingId: rid,
      duration: this.formatDurationForApi(this.totalRecordingTime),
      specialty: this.patientInfo.neurologicalSpecialty?.trim() || this.defaultNeurologicalSpecialty,
      appointmentType: this.patientInfo.appointmentType?.trim() || this.defaultAppointmentType
    };
    if (this.resumeTranscriptionDbId != null) {
      body.existingPausedTranscriptionId = this.resumeTranscriptionDbId;
    }
    if (this.selectedPatient) {
      body.patientId = this.selectedPatient.id;
      if (useModalName) {
        const n = this.pauseModalPatientName?.trim();
        if (n) {
          body.patientName = n;
        }
      }
    } else {
      const modalName = (this.pauseModalPatientName ?? '').trim();
      const openDraft = this.editingRecordingPatient ? this.recordingPatientNameDraft.trim() : '';
      const screenName =
        (!this.isUnknownDisplayName(openDraft) ? openDraft : '') ||
        this.selectedPatientDisplay?.trim() ||
        this.patientInfo.patientName?.trim() ||
        '';
      // A saved edit sets the label and skips the pause dialog, leaving the dialog field empty.
      // An empty dialog must not replace that name. A name still in the editor counts too.
      const typed = useModalName
        ? modalName || (this.isUnknownDisplayName(screenName) ? '' : screenName)
        : (this.selectedPatientDisplay || '').trim() ||
          (!this.isUnknownDisplayName(openDraft) ? openDraft : '');
      if (typed) {
        body.patientName = typed;
      } else if (this.draftPatientIdForUnknown != null) {
        body.patientId = this.draftPatientIdForUnknown;
      } else {
        body.patientName = this.defaultPatientName;
      }
    }
    this.recordingService.pauseRecordingDraft(body).subscribe({
      next: (res) => {
        this.ngZone.run(() => {
          if (this.selectedPatient || (useModalName && !!this.pauseModalPatientName?.trim())) {
            this.visitPauseNameLocked = true;
          }
          if (res.patientId != null) {
            this.draftPatientIdForUnknown = res.patientId;
          }
          const resId = res.id != null ? Number.parseInt(String(res.id), 10) : NaN;
          if (Number.isFinite(resId) && this.resumeTranscriptionDbId == null) {
            this.resumeTranscriptionDbId = resId;
          }
          mergePauseVisitIntoManifest(rid, {
            accumulatedDurationSeconds: this.totalRecordingTime,
            pausedTranscriptionDbId: this.resumeTranscriptionDbId ?? (Number.isFinite(resId) ? resId : undefined),
            visitPatientNameLocked: this.visitPauseNameLocked,
            draftPatientId: this.draftPatientIdForUnknown ?? undefined
          });
          this.manifestSnapshotBeforeResumedTake = null;
          this.showPauseForLaterDialog = false;
          this.tearDownRecorderAfterPauseSave();
          const pausedVisitId =
            this.resumeTranscriptionDbId ?? (Number.isFinite(resId) ? resId : res.id);
          // Compact shell hides the list while a visit is selected. Leave selection unset so Pause
          // shows every visit, matching the desktop list pane.
          if (!this.isCompactShell()) {
            this.transcriptionsService.requestSelectVisitOnLoad(pausedVisitId);
          }
          this.transcriptionsService.requestTranscriptionsListRefresh();
          this.pauseModalOpenedFrom = null;
          if (this.routeLeaveResolver) {
            this.routeLeaveResolver.next(true);
            this.routeLeaveResolver.complete();
            this.routeLeaveResolver = null;
          }
          const navTarget = this.postPauseNavigateTarget;
          this.postPauseNavigateTarget = null;
          if (navTarget === 'freshRecording') {
            this.resetRecordingWorkspaceForNewVisit();
          } else if (navTarget === 'transcriptions') {
            if (this.isCompactShell()) {
              void this.router.navigate(this.listAfterRecordingPath(), {
                queryParams: { list: '1' },
              });
            } else {
              this.navigateToList();
            }
          }
        });
      },
      error: (err: Error) => {
        this.ngZone.run(() => {
          this.savingPausedVisit = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Could not save paused visit',
            detail: err?.message ?? 'Try again.'
          });
          this.cdr.detectChanges();
        });
      }
    });
  }

  private tearDownRecorderAfterPauseSave(): void {
    this.silentRecorderTeardownDone = true;
    this.periodicFlushActive = false;
    this.screenWakeLockEpoch++;
    void this.releaseScreenWakeLock();
    this.hasSavedCurrentRecording = true;
    this.isStoppingRecording = false;
    this.isRecording = false;
    this.isPaused = false;
    this.stopTimer();
    this.clearSafariSegmentTimer();
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.onstop = null;
        this.mediaRecorder.stop();
      } catch {
        // ignore
      }
    }
    this.mediaRecorder = null;
    this.cleanupAudio();
    this.audioChunks = [];
    this.syncCaptureActive();
  }

  private syncCaptureActive(): void {
    this.recordingSession.setCaptureActive(this.isRecording || this.isPaused);
  }

  /**
   * iOS can mute or end the WKWebView mic when the app becomes active again. If the track
   * does not recover, open a new one on the same visit so background/foreground keeps recording.
   */
  private async restoreCaptureAfterForeground(): Promise<void> {
    const generation = ++this.foregroundRestoreGeneration;
    const track = this.audioStream?.getAudioTracks()[0];
    if (track?.muted && track.readyState === 'live') {
      await new Promise<void>((resolve) => {
        const done = () => {
          track.removeEventListener('unmute', done);
          resolve();
        };
        track.addEventListener('unmute', done);
        window.setTimeout(done, 800);
      });
    } else {
      await new Promise((resolve) => window.setTimeout(resolve, 300));
    }
    if (generation !== this.foregroundRestoreGeneration) {
      return;
    }
    if (!this.isRecording || this.isPaused || this.isStoppingRecording || this.destroyingComponent) {
      return;
    }

    const liveTrack = this.audioStream?.getAudioTracks()[0];
    const recorder = this.mediaRecorder;
    const trackLive = !!liveTrack && liveTrack.readyState === 'live' && !liveTrack.muted;
    if (trackLive && recorder?.state === 'recording') {
      this.resumeOrRebuildAudioGraph();
      this.postNativeRecordingState(true);
      return;
    }
    if (trackLive && recorder?.state === 'paused') {
      try {
        recorder.resume();
        this.scheduleNextSafariSegment();
        this.resumeOrRebuildAudioGraph();
        this.postNativeRecordingState(true);
        return;
      } catch {
        // The track survived but the recorder did not. Replace both below.
      }
    }
    await this.restartLiveCapture();
  }

  private resumeOrRebuildAudioGraph(): void {
    const state = this.audioContext?.state;
    if (state === 'suspended') {
      void this.audioContext?.resume();
      return;
    }
    if (state === 'closed' || !this.audioContext) {
      this.keepAliveOsc = null;
      this.audioContext = null;
      this.audioAnalyser = null;
      this.setupAudioAnalysis();
      this.startRecordingKeepAlive();
    }
  }

  /**
   * Replace the live mic without ending the visit or resetting chunk indexes.
   * `unavailable` means the requested device could not be opened and the previous mic is untouched.
   */
  private async restartLiveCapture(options?: {
    keepPaused?: boolean;
    allowDeviceFallback?: boolean;
  }): Promise<'swapped' | 'unavailable' | 'skipped'> {
    const keepPaused = options?.keepPaused === true;
    const allowDeviceFallback = options?.allowDeviceFallback !== false;
    if (this.captureRestartRecorder || this.isStoppingRecording || !this.captureSwapStillValid(keepPaused)) {
      return 'skipped';
    }
    let newStream: MediaStream;
    try {
      newStream = await this.openMicrophoneStream(allowDeviceFallback);
    } catch (err) {
      console.warn('Could not open the microphone', err);
      return 'unavailable';
    }
    if (!this.captureSwapStillValid(keepPaused)) {
      newStream.getTracks().forEach((track) => track.stop());
      return 'skipped';
    }

    this.clearSafariSegmentTimer();
    await this.waitForRecorderStopIdle();
    if (!this.captureSwapStillValid(keepPaused)) {
      newStream.getTracks().forEach((track) => track.stop());
      return 'skipped';
    }

    const oldRecorder = this.mediaRecorder;
    if (oldRecorder && oldRecorder.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        const finish = () => {
          if (this.resolveCaptureRestart === finish) {
            this.resolveCaptureRestart = null;
          }
          resolve();
        };
        this.resolveCaptureRestart = finish;
        this.captureRestartRecorder = oldRecorder;
        this.beginRecorderStop();
        this.armSegmentDataWait();
        try {
          oldRecorder.stop();
        } catch (err) {
          console.warn('MediaRecorder.stop failed during foreground restore', err);
          if (this.captureRestartRecorder === oldRecorder) {
            this.captureRestartRecorder = null;
          }
          this.resolveCaptureRestart = null;
          this.resolveSegmentDataIfAwaiting();
          this.releaseRecorderStopBarrier();
          resolve();
        }
        window.setTimeout(finish, 5000);
      });
    }

    if (!this.captureSwapStillValid(keepPaused)) {
      newStream.getTracks().forEach((track) => track.stop());
      return 'skipped';
    }

    this.audioStream?.getTracks().forEach((track) => track.stop());
    try {
      this.keepAliveOsc?.stop();
    } catch {
      // Oscillator was already stopped.
    }
    this.keepAliveOsc = null;
    const previousContext = this.audioContext;
    this.audioContext = null;
    this.audioAnalyser = null;
    if (previousContext && previousContext.state !== 'closed') {
      void previousContext.close();
    }
    this.audioStream = newStream;
    this.setupAudioAnalysis();
    if (keepPaused) {
      this.mediaRecorder = null;
      this.cdr.detectChanges();
      return 'swapped';
    }
    this.startRecordingKeepAlive();
    this.startRecorderOnCurrentStream();
    this.cdr.detectChanges();
    return 'swapped';
  }

  private captureSwapStillValid(keepPaused: boolean): boolean {
    if (this.isStoppingRecording || this.destroyingComponent) {
      return false;
    }
    if (keepPaused) {
      return this.isPaused;
    }
    return this.isRecording && !this.isPaused;
  }

  private startRecorderOnCurrentStream(): void {
    if (!this.audioStream) {
      return;
    }
    const format = recorderFormatForMimeType(this.activeRecordingMimeType);
    try {
      this.mediaRecorder = format.mimeType
        ? new MediaRecorder(this.audioStream, { mimeType: format.mimeType })
        : new MediaRecorder(this.audioStream);
      const effective = this.mediaRecorder.mimeType || format.mimeType;
      if (effective) {
        this.activeRecordingMimeType = effective;
      }
      this.attachMediaRecorderHandlers(this.mediaRecorder);
      if (this.usesSafariSegmentedRecording()) {
        this.mediaRecorder.start();
        this.scheduleNextSafariSegment();
      } else {
        this.mediaRecorder.start(checkpointTimesliceMs(this.activeRecordingMimeType));
      }
    } catch (err) {
      console.warn('Failed to restart recording after foreground', err);
    }
  }

  /**
   * iOS suspends WKWebView capture when the app is backgrounded unless the process is an
   * active audio app. A near-silent tone plus a native silent player keep the mic open.
   */
  private startRecordingKeepAlive(): void {
    this.postNativeRecordingState(true);
    const ctx = this.audioContext;
    if (!ctx || this.keepAliveOsc) {
      return;
    }
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0.0001;
      osc.frequency.value = 20;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      this.keepAliveOsc = osc;
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }
    } catch {
      // The native player still holds the session if this graph cannot start.
    }
  }

  private stopRecordingKeepAlive(): void {
    this.postNativeRecordingState(false);
    try {
      this.keepAliveOsc?.stop();
    } catch {
      // Oscillator was already stopped.
    }
    this.keepAliveOsc = null;
  }

  private postNativeRecordingState(active: boolean): void {
    const webkit = (
      window as unknown as {
        webkit?: { messageHandlers?: { scryberRecording?: { postMessage: (body: string) => void } } };
      }
    ).webkit;
    webkit?.messageHandlers?.scryberRecording?.postMessage(active ? '1' : '0');
  }

  /** After "New recording" confirm pause: clear visit state so the next capture is a new active session. */
  private resetRecordingWorkspaceForNewVisit(options?: { afterDiscardedAppend?: boolean }): void {
    this.recordingId = null;
    this.audioChunksLogicalBase = 0;
    this.nextCheckpointBatchNumber = 1;
    this.resumeTranscriptionDbId = null;
    this.pendingResumeRecordingUuid = null;
    this.appendTranscriptionDbId = null;
    this.appendCanonicalRecordingUuid = null;
    this.appendVisitHasSoap = false;
    this.preservedManifestBeforeAppend = null;
    this.manifestSnapshotBeforeResumedTake = null;
    this.visitPauseNameLocked = false;
    this.draftPatientIdForUnknown = null;
    this.hasAttemptedRecording = false;
    this.silentRecorderTeardownDone = false;
    this.selectedPatient = null;
    this.selectedPatientDisplay = '';
    this.patientInfo = {
      patientName: '',
      mrn: '',
      appointmentType: this.defaultAppointmentType,
      neurologicalSpecialty: this.defaultNeurologicalSpecialty
    };
    this.isMrnReadonly = false;
    this.resetTimer();
    this.syncCaptureActive();
    if (options?.afterDiscardedAppend) {
      this.messageService.add({
        severity: 'info',
        summary: 'Addition discarded',
        detail: 'Starting a new recording. The existing visit was left unchanged.'
      });
    } else {
      this.messageService.add({
        severity: 'success',
        summary: 'Paused',
        detail: 'Previous visit saved. Press Record when you are ready for a new one.'
      });
    }
    this.replaceWithFreshRecording();
    this.cdr.detectChanges();
  }

  private transcriptionHasGeneratedSoap(detail: TranscriptionDetailResponse): boolean {
    const soap = detail.analysis?.soapNote;
    if (!soap) {
      return false;
    }
    const textParts = [soap.subjective, soap.objective, soap.patientInstructions].map((p) =>
      (p ?? '').toString().trim()
    );
    if (textParts.some((t) => t.length > 0)) {
      return true;
    }
    return (soap.problems ?? []).some(
      (p) =>
        !!(p.assessment ?? '').toString().trim() ||
        (p.plan ?? []).some((item) => !!(item ?? '').toString().trim()) ||
        !!(p.name ?? '').toString().trim()
    );
  }

  private loadAppendRecordingSession(transcriptionId: string): void {
    this.transcriptionsService.getTranscriptionById(transcriptionId).subscribe({
      next: (detail) => {
        this.shortRecordingAwaitingContinue = false;
        this.appendTranscriptionDbId = null;
        this.appendCanonicalRecordingUuid = null;
        this.appendVisitHasSoap = false;
        if (!detail) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Visit not found',
            detail: 'Open this action from the transcriptions list or detail page.'
          });
          return;
        }
        const st = (detail.status ?? '').toLowerCase();
        if (st !== 'completed' && st !== 'needs review' && st !== 'failed') {
          this.messageService.add({
            severity: 'info',
            summary: 'Recording unavailable',
            detail: 'You can only add audio to completed, needs-review, or failed visits that have a final recording.'
          });
          return;
        }
        if (!isCheckpointFinalRecordingKey(detail.originalFileName)) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Cannot extend this visit',
            detail: 'This recording is not in the extendable format. Start a new recording instead.'
          });
          return;
        }
        const dbId = Number.parseInt(transcriptionId, 10);
        if (!Number.isFinite(dbId)) {
          return;
        }
        const canonicalUuid = parseCheckpointFinalRecordingUuid(detail.originalFileName);
        if (!canonicalUuid) {
          return;
        }
        this.appendTranscriptionDbId = dbId;
        this.appendCanonicalRecordingUuid = canonicalUuid;
        this.appendVisitHasSoap = this.transcriptionHasGeneratedSoap(detail);
        this.resumeTranscriptionDbId = null;
        this.pendingResumeRecordingUuid = null;

        const secApi = this.parseDurationToSeconds(detail.duration);
        this.totalRecordingTime = secApi;
        this.recordingTime = this.formatClockFromSeconds(this.totalRecordingTime);
        this.updateTimerDisplay();

        this.visitPauseNameLocked = true;
        this.patientInfo.patientName = (detail.patientName ?? '').trim();
        this.patientInfo.mrn = (detail.mrn ?? '').trim();
        if (detail.appointmentType?.trim()) {
          this.patientInfo.appointmentType = detail.appointmentType.trim();
        }
        if (detail.specialty?.trim()) {
          this.patientInfo.neurologicalSpecialty = detail.specialty.trim();
        }
        this.selectedPatient = null;
        this.selectedPatientDisplay = (detail.patientName ?? '').trim();
        this.isMrnReadonly = !!this.patientInfo.mrn;

        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { appendTranscription: null },
          replaceUrl: true
        });
        this.cdr.detectChanges();
        void this.beginAutoStartRecordingAfterQueryLoad();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Could not open visit',
          detail: 'Try again from the transcriptions list.'
        });
      }
    });
  }

  /** After append or paused-resume query loads, start the mic and confirm with a short toast. */
  private async beginAutoStartRecordingAfterQueryLoad(): Promise<void> {
    await this.onStartRecording();
    if (!this.isRecording) {
      return;
    }
    this.messageService.add({
      severity: 'success',
      summary: 'Recording started'
    });
    this.cdr.detectChanges();
  }

  private loadPausedResumeDraft(transcriptionId: string): void {
    this.transcriptionsService.getTranscriptionById(transcriptionId).subscribe({
      next: (detail) => {
        this.manifestSnapshotBeforeResumedTake = null;
        this.shortRecordingAwaitingContinue = false;
        if (!detail?.originalFileName) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Cannot resume',
            detail: 'This visit is missing draft metadata.'
          });
          return;
        }
        const st = (detail.status ?? '').trim().toLowerCase();
        // Generated notes: treat Resume as "add more" (append), not paused-draft resume.
        if (st !== 'paused') {
          if (
            (st === 'completed' || st === 'needs review' || st === 'failed') &&
            isCheckpointFinalRecordingKey(detail.originalFileName)
          ) {
            this.loadAppendRecordingSession(transcriptionId);
            return;
          }
          this.messageService.add({
            severity: 'info',
            summary: 'Resume unavailable',
            detail: 'This visit is still processing. Resume is available after the note is ready, or for paused visits.'
          });
          return;
        }
        const marker = 'paused-recording:';
        const fn = detail.originalFileName.trim().toLowerCase();
        if (!fn.startsWith(marker)) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Cannot resume',
            detail: 'Unexpected draft reference.'
          });
          return;
        }
        const uuid = detail.originalFileName.trim().slice(marker.length).trim().toLowerCase();
        const manifest = readRecordingCheckpointById(uuid);
        if (!manifest) {
          this.restorePausedDraftFromServer(transcriptionId, uuid, detail);
          return;
        }
        this.serverCheckpointContainerMismatch = false;
        this.openPausedResume(transcriptionId, uuid, detail, manifest, true);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Resume failed',
          detail: 'Could not load this visit.'
        });
      }
    });
  }

  /**
   * Local draft is gone. If checkpoint batches are still on the server, rebuild the manifest from them so the
   * visit can be continued (same container) or ended and transcribed.
   */
  private restorePausedDraftFromServer(
    transcriptionId: string,
    uuid: string,
    detail: TranscriptionDetailResponse
  ): void {
    const dbId = Number.parseInt(transcriptionId, 10);
    if (!Number.isFinite(dbId)) {
      this.enterOrphanPausedResumeState(transcriptionId, detail);
      return;
    }
    this.restoringPausedFromServer = true;
    this.orphanedPausedResume = null;
    this.cdr.detectChanges();
    this.recordingService.getPausedCheckpointStatus(uuid, dbId).subscribe({
      next: (status) => {
        this.ngZone.run(() => {
          this.restoringPausedFromServer = false;
          const restored = this.manifestFromServerCheckpoints(uuid, dbId, detail, status);
          if (!restored) {
            this.enterOrphanPausedResumeState(transcriptionId, detail);
            return;
          }
          const canContinue = browserCanContinueCheckpointContainer(status.fileExtension);
          this.serverCheckpointContainerMismatch = !canContinue;
          this.openPausedResume(transcriptionId, uuid, detail, restored, canContinue);
          if (!canContinue) {
            this.messageService.add({
              severity: 'info',
              summary: 'Saved audio loaded',
              detail:
                'Audio for this visit was loaded from the server. End the visit to transcribe it. This browser records in a different format, so it can’t add more.',
            });
          }
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.restoringPausedFromServer = false;
          this.enterOrphanPausedResumeState(transcriptionId, detail);
        });
      },
    });
  }

  private manifestFromServerCheckpoints(
    uuid: string,
    pausedTranscriptionDbId: number,
    detail: TranscriptionDetailResponse,
    status: PausedCheckpointStatus | null
  ): RecordingCheckpointState | null {
    if (!status || status.batchCount < 1 || status.highestBatchIndex < 1) {
      return null;
    }
    const seconds = this.parseDurationToSeconds(detail.duration);
    const now = Date.now();
    return {
      recordingId: uuid,
      recordingStartedAt: now - seconds * 1000,
      lastUploadAt: now,
      lastChunkIndex: -1,
      accumulatedDurationSeconds: seconds,
      pausedTranscriptionDbId,
      visitPatientNameLocked: !this.isUnknownDisplayName(detail.patientName),
      draftPatientId: detail.patientId ?? undefined,
      nextCheckpointBatchNumber: status.highestBatchIndex + 1,
      recorderMimeType: mimeTypeForCheckpointExtension(status.fileExtension),
    };
  }

  /** Apply a paused draft (local or restored from the server) and optionally start the microphone. */
  private openPausedResume(
    transcriptionId: string,
    uuid: string,
    detail: TranscriptionDetailResponse,
    manifest: RecordingCheckpointState,
    autoStart: boolean
  ): void {
    this.activatePausedDraft(manifest);
    const dbId = Number.parseInt(transcriptionId, 10);
    if (!Number.isFinite(dbId)) {
      return;
    }
    this.orphanedPausedResume = null;
    this.appendTranscriptionDbId = null;
    this.appendCanonicalRecordingUuid = null;
    this.appendVisitHasSoap = false;
    this.resumeTranscriptionDbId = dbId;
    this.pendingResumeRecordingUuid = uuid;

    const unknown = this.isUnknownDisplayName(detail.patientName);
    this.draftPatientIdForUnknown =
      manifest.draftPatientId ?? (unknown && detail.patientId != null ? detail.patientId : null);
    this.visitPauseNameLocked = !!(manifest.visitPatientNameLocked || !unknown);
    const secApi = this.parseDurationToSeconds(detail.duration);
    const secMan = manifest.accumulatedDurationSeconds ?? 0;
    this.totalRecordingTime = Math.max(secApi, secMan);
    this.recordingTime = this.formatClockFromSeconds(this.totalRecordingTime);
    this.updateTimerDisplay();

    mergePauseVisitIntoManifest(uuid, {
      pausedTranscriptionDbId: dbId,
      accumulatedDurationSeconds: this.totalRecordingTime,
      visitPatientNameLocked: this.visitPauseNameLocked,
      draftPatientId: this.draftPatientIdForUnknown ?? undefined
    });

    this.patientInfo.patientName = (detail.patientName ?? '').trim();
    this.patientInfo.mrn = (detail.mrn ?? '').trim();
    if (detail.appointmentType?.trim()) {
      this.patientInfo.appointmentType = detail.appointmentType.trim();
    }
    if (detail.specialty?.trim()) {
      this.patientInfo.neurologicalSpecialty = detail.specialty.trim();
    }
    this.selectedPatient = null;
    this.selectedPatientDisplay = (detail.patientName ?? '').trim();
    this.isMrnReadonly = !!this.patientInfo.mrn;
    this.activeRecordingMimeType = manifest.recorderMimeType || this.activeRecordingMimeType;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { resumeTranscription: null },
      replaceUrl: true
    });
    this.cdr.detectChanges();
    if (autoStart) {
      void this.beginAutoStartRecordingAfterQueryLoad();
    }
  }

  /** Make a parked paused draft the active checkpoint so further slices update that visit. */
  private activatePausedDraft(manifest: RecordingCheckpointState): void {
    const active = readRecordingCheckpoint();
    if (active && active.recordingId.toLowerCase() !== manifest.recordingId.toLowerCase()) {
      releaseActiveCheckpointForNewVisit();
    }
    restoreRecordingCheckpointManifest(manifest);
  }

  private enterOrphanPausedResumeState(transcriptionId: string, detail: TranscriptionDetailResponse): void {
    this.restoringPausedFromServer = false;
    this.orphanedPausedResume = {
      transcriptionId,
      originalFileName: detail.originalFileName ?? '',
      patientDisplay: (detail.patientName ?? '').trim() || 'this paused visit'
    };
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { resumeTranscription: null },
      replaceUrl: true
    });
    this.cdr.detectChanges();
  }

  deleteOrphanPausedRecordingFromBanner(): void {
    const o = this.orphanedPausedResume;
    if (!o || this.orphanPausedDeleteBusy) {
      return;
    }
    this.showDeleteOrphanPausedConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onCancelDeleteOrphanPausedRecording(): void {
    if (this.orphanPausedDeleteBusy) {
      return;
    }
    this.showDeleteOrphanPausedConfirmDialog = false;
    this.cdr.detectChanges();
  }

  onConfirmDeleteOrphanPausedRecording(): void {
    const o = this.orphanedPausedResume;
    if (!o || this.orphanPausedDeleteBusy) {
      return;
    }
    this.showDeleteOrphanPausedConfirmDialog = false;
    this.orphanPausedDeleteBusy = true;
    this.transcriptionsService
      .deleteTranscription(o.transcriptionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          clearLocalStorageForPausedTranscriptionDraft(o.originalFileName);
          this.orphanPausedDeleteBusy = false;
          this.orphanedPausedResume = null;
          this.transcriptionsService.requestTranscriptionsListRefresh();
          this.messageService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: 'Paused visit removed.'
          });
          this.cdr.detectChanges();
        },
        error: () => {
          this.orphanPausedDeleteBusy = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Delete failed',
            detail: 'Could not remove this visit. Try again.'
          });
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * After upload returns, stay on the list and summarize status (list row is source of truth).
   */
  private showTranscriptionUploadResultToast(response: TranscriptionResponse): void {
    const status = (response.status ?? '').toString().trim().toUpperCase();
    if (status === 'FAILED' || status === 'ERROR') {
      this.messageService.add({
        severity: 'error',
        summary: 'Transcription failed',
        detail: response.error?.trim() || 'See the transcriptions list for details.'
      });
      return;
    }
    if (status === 'COMPLETED' || status === 'COMPLETE') {
      this.messageService.add({
        severity: 'success',
        summary: 'Completed',
        detail: 'Transcription is ready in the list.'
      });
      return;
    }
    if (status === 'NEEDS REVIEW' || status === 'NEEDS_REVIEW') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Needs review',
        detail: 'This visit is marked needs review in the list.'
      });
      return;
    }
    this.messageService.add({
      severity: 'info',
      summary: 'Processing',
      detail: 'Transcription is in progress. Watch the status column on the list.'
    });
  }

  isFormReady(): boolean {
    return true;
  }

  // Get recording status text
  getRecordingStatusText(): string {
    if (!this.isRecording) return '';
    if (this.resumeTranscriptionDbId != null) {
      if (this.isPaused) return 'Paused — tap Resume to continue this visit';
      return 'Recording — adding to your resumed visit';
    }
    if (this.appendTranscriptionDbId != null) {
      if (this.isPaused) return 'Paused — tap Resume to continue';
      if (this.appendVisitHasSoap) {
        return 'Recording in progress....pause not available on this visit';
      }
      return 'Recording — new audio will be merged into this visit and SOAP regenerated';
    }
    if (this.isPaused) return 'Recording paused';
    return 'Recording in progress...';
  }


}
